import { tasksInProjectFolder } from './utils/projectFolders';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, List, Menu, Modal, Select, message,Button } from 'antd';
import {
  AudioOutlined,
  BookOutlined, CalendarOutlined, CaretDownOutlined, CaretRightOutlined,
  CloseCircleOutlined, CloseOutlined,
  ClockCircleOutlined, DatabaseOutlined, DownloadOutlined,
  DownOutlined, EditOutlined, FolderAddOutlined, FolderOpenOutlined, FolderOutlined,
  FilePdfOutlined, FileTextOutlined, FileWordOutlined, EyeOutlined,
  LinkOutlined, LoadingOutlined, LogoutOutlined, MessageOutlined, MoreOutlined, ShareAltOutlined,
  CopyOutlined, CheckOutlined,
  PlayCircleOutlined,
  RocketOutlined,
  StarFilled, StarOutlined,
  MenuOutlined,
  PaperClipOutlined, PlusOutlined, ProjectOutlined, QuestionCircleOutlined,
  SearchOutlined, SendOutlined, SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as api from './api/client';
import type { AgentTask, TaskLibraryItem } from './types';
import logoHandBtn from './assets/logo_hand_button.png';
import logoHandBtnDark from './assets/logo_hand_button_dark.png';
import type { GoogleUser } from './App';
import AdminView from './components/AdminView';
import AgentActivityConsole from './components/AgentActivityConsole';
import AgentsView from './components/AgentsView';
import ChatWorkspace from './components/ChatWorkspace';
import DictationOverlay from './components/DictationOverlay';
import HelpView from './components/HelpView';
import HelpAssistant from './components/HelpAssistant';
import KnowledgeBaseManager from './components/KnowledgeBaseManager';
import PrivacyPolicyView from './components/PrivacyPolicyView';
import ProfileView from './components/ProfileView';
import FeatureRequestsHub from './components/FeatureRequestsHub';
import TermsOfServiceView from './components/TermsOfServiceView';
import WaveIndicator from './components/WaveIndicator';
import AddToKnowledgeBaseButton from './components/AddToKnowledgeBaseButton';
import { summarizeTaskPrompt } from './taskPromptSummary';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  onSelectTask: (taskId: string) => void;
  activeTaskId?: string | null;
  onClearTask?: () => void;
  user: GoogleUser | null;
  onLogout?: () => void;
  isAdmin?: boolean;
  isLightMode?: boolean;
}

interface Project {
  id: string;
  name: string;
  instructions?: string;
  taskIds: string[];
  collapsed: boolean;
}

interface ContextMenuState {
  x: number;
  y: number;
  targetId: string;
  targetType: 'task' | 'chat';
}

interface ScheduledTask {
  id: string;
  goal: string;
  agentType: string;
  isRecurring: boolean;
  scheduledAt: string;       // ISO string for one-time tasks
  recurringDays: number[];   // 0=Sun … 6=Sat for recurring tasks
  recurringTime: string;     // "HH:MM" for recurring tasks
}

interface MemoryEntrySummary {
  id: string;
  text: string;
}

interface SuggestedTaskStarter {
  id: string;
  knowledge_base_id: string;
  name: string;
  description?: string;
  suggested_prompt?: string;
  recommended_mode?: 'chat' | 'deep_search' | 'autonomous';
  suggested_knowledge_base_ids?: string[];
  tags?: string[];
}

interface SecretNameSuggestion {
  secret_name: string;
  category: api.SecretCategory;
  masked_preview: string;
  risk_level: api.SecretRiskLevel;
}

interface TaskShare {
  id: string;
  task_id: string;
  owner_user_id?: string;
  shared_with_user_id: string;
  permission: string;
  created_at?: string;
  updated_at?: string;
}

type View = 'home' | 'search' | 'library' | 'schedules' | 'chat' | 'project' | 'knowledge' | 'profile' | 'help' | 'admin' | 'features' | 'agents' | 'terms' | 'privacy';
type ChatMessage = { role: 'user' | 'assistant'; content: string };
type ChatSessionSummary = { id: string; title: string; updatedAt: string };
type TaskPromptMode = 'standard' | 'deep_search';
type DictationTarget = 'task' | 'chat';
type DictationStatus = 'idle' | 'loading' | 'recording' | 'stopped' | 'error' | 'permission-denied';
type MentionTarget = 'task' | 'chat';

interface SecretMentionContext {
  target: MentionTarget;
  start: number;
  end: number;
  query: string;
}

interface SecretMentionDropdownPosition {
  left: number;
  top: number;
  width: number;
}

interface SpeechRecognitionResultLike {
  0: { transcript: string };
  isFinal: boolean;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultLike[];
}

interface SpeechRecognitionErrorEventLike {
  error?: string;
  message?: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onstart?: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_DOT_COLOR: Record<string, string> = {
  COMPLETED: '#10b981',
  RUNNING: '#3b82f6',
  FAILED: '#ef4444',
  PAUSED: '#f59e0b',
  PLANNING: '#8b5cf6',
  CANCELLED: '#6b7280',
  CREATED: '#6b7280',
  PLAN_READY: '#6366f1',
  WAITING_FOR_APPROVAL: '#f59e0b',
};

const SUGGESTIONS = [
  { icon: '🔍', text: 'Research the latest AI safety papers and summarize key findings' },
  { icon: '📊', text: 'Analyze market trends in the renewable energy sector' },
  { icon: '📝', text: 'Draft a technical specification document for a REST API' },
  { icon: '🌐', text: 'Scrape and compare pricing from competitor websites', hasDialog: true },
];

const VERSIONS = [
  { id: 'lite-1.5', label: 'Praxys 1.5 lite', desc: 'A lightweight agent for everyday tasks.' },
  { id: 'pro-1.5', label: 'Praxys 1.5 pro', desc: 'Full-featured agent with advanced tools.' },
];

const TASK_ATTACHMENT_ACCEPT = [
  '.pdf', '.doc', '.docx', '.txt', '.csv', '.json', '.jsonl', '.md', '.xlsx',
  '.sav', '.pptx', '.yaml', '.yml', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.bmp', '.tif', '.tiff',
].join(',');
const TASK_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff']);

const isTaskImageAttachment = (file: File): boolean => {
  if (file.type && file.type.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return TASK_IMAGE_EXTENSIONS.has(ext);
};

const noop = () => {};

const PROJECTS_STORAGE_KEY = 'praxys_projects_v1';
const PROJECTS_MEMORY_NAMESPACE = 'praxys_projects_state_v1';
const MAX_PROJECT_SNAPSHOTS = 10;
const PROJECTS_VISIBLE_LIMIT = 7;
const SCHEDULED_TASKS_KEY  = 'praxys_scheduled_tasks_v1';
const CHAT_MESSAGES_KEY    = 'praxys_chat_messages_v2';
const CHAT_SESSIONS_KEY    = 'praxys_chat_sessions_v1';
const CHAT_ACTIVE_SESSION_KEY = 'praxys_chat_active_session_v1';
const CHAT_ARCHIVED_SESSION_IDS_KEY = 'praxys_chat_archived_session_ids_v1';
const LIBRARY_FAVORITES_KEY = 'praxys_library_favorites_v1';
const MAX_DOWNLOAD_FILENAME_LENGTH = 60;
const CHAT_KB_SEARCH_LIMIT = 6;
const MAX_RECENT_TASKS = 20;
const MOBILE_BREAKPOINT = 768;
const LIBRARY_PAGE_SIZE = 12;
const SEARCH_PAGE_SIZE = 12;

const copyTextareaStyle = (source: CSSStyleDeclaration, target: CSSStyleDeclaration) => {
  target.boxSizing = source.boxSizing;
  target.width = source.width;
  target.height = source.height;
  target.overflowX = source.overflowX;
  target.overflowY = source.overflowY;
  target.borderTopWidth = source.borderTopWidth;
  target.borderRightWidth = source.borderRightWidth;
  target.borderBottomWidth = source.borderBottomWidth;
  target.borderLeftWidth = source.borderLeftWidth;
  target.borderStyle = source.borderStyle;
  target.paddingTop = source.paddingTop;
  target.paddingRight = source.paddingRight;
  target.paddingBottom = source.paddingBottom;
  target.paddingLeft = source.paddingLeft;
  target.fontStyle = source.fontStyle;
  target.fontVariant = source.fontVariant;
  target.fontWeight = source.fontWeight;
  target.fontStretch = source.fontStretch;
  target.fontSize = source.fontSize;
  target.fontFamily = source.fontFamily;
  target.lineHeight = source.lineHeight;
  target.letterSpacing = source.letterSpacing;
  target.textAlign = source.textAlign;
  target.textTransform = source.textTransform;
  target.textIndent = source.textIndent;
  target.wordBreak = source.wordBreak;
  target.wordSpacing = source.wordSpacing;
  target.tabSize = source.tabSize;
  target.whiteSpace = 'pre-wrap';
};

const getTextareaCaretPosition = (textarea: HTMLTextAreaElement, index: number) => {
  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const marker = document.createElement('span');
  mirror.setAttribute('aria-hidden', 'true');
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.pointerEvents = 'none';
  mirror.style.zIndex = '-1';
  copyTextareaStyle(computed, mirror.style);
  mirror.style.width = `${textarea.clientWidth}px`;
  mirror.textContent = textarea.value.slice(0, index);
  marker.textContent = textarea.value.slice(index, index + 1) || ' ';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const markerRect = marker.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const lineHeight = Number.parseFloat(computed.lineHeight) || Number.parseFloat(computed.fontSize) * 1.4 || 20;
  const left = markerRect.left - mirrorRect.left - textarea.scrollLeft;
  const top = markerRect.top - mirrorRect.top - textarea.scrollTop;
  document.body.removeChild(mirror);
  return { left, top, lineHeight };
};

const getMentionDropdownWidth = (textarea: HTMLTextAreaElement, secretNames: string[]) => {
  const longestSecretName = secretNames.reduce((longest, current) => (
    current.length > longest.length ? current : longest
  ), '');
  if (!longestSecretName) return 180;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return Math.max(180, (longestSecretName.length + 3) * 8);
  const computed = window.getComputedStyle(textarea);
  context.font = `600 12px ${computed.fontFamily || 'sans-serif'}`;
  const textWidth = context.measureText(`@${longestSecretName}`).width;
  return Math.ceil(textWidth + 28);
};

// ── LocalStorage helpers ──────────────────────────────────────────────────────

const _storageKeyForUser = (baseKey: string, userId?: string): string => {
  const normalized = (userId || '').trim().toLowerCase();
  return `${baseKey}:${normalized}`;
};

const redactSecretReferences = (text: string): string =>
  text.replace(/@([A-Z][A-Z0-9_]*)/g, '@[SECRET]');

const loadProjects = (userId?: string): Project[] => {
  if (!userId?.trim()) return [];
  try {
    return JSON.parse(localStorage.getItem(_storageKeyForUser(PROJECTS_STORAGE_KEY, userId)) || '[]');
  } catch {
    return [];
  }
};

const saveProjects = (userId: string | undefined, projects: Project[]) => {
  if (!userId?.trim()) return;
  localStorage.setItem(_storageKeyForUser(PROJECTS_STORAGE_KEY, userId), JSON.stringify(projects));
};

const loadScheduledTasks = (): ScheduledTask[] => {
  try {
    return JSON.parse(localStorage.getItem(SCHEDULED_TASKS_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveScheduledTasks = (tasks: ScheduledTask[]) => {
  const redactedTasks = tasks.map(task => ({
    ...task,
    goal: redactSecretReferences(task.goal),
  }));
  localStorage.setItem(SCHEDULED_TASKS_KEY, JSON.stringify(redactedTasks));
};

const _chatMessagesStorageKey = (userId: string | undefined, sessionId: string): string =>
  _storageKeyForUser(`${CHAT_MESSAGES_KEY}:${sessionId}`, userId);

const loadChatMessages = (userId: string | undefined, sessionId: string): ChatMessage[] => {
  if (!userId?.trim() || !sessionId.trim()) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(_chatMessagesStorageKey(userId, sessionId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveChatMessages = (userId: string | undefined, sessionId: string, msgs: ChatMessage[]) => {
  if (!userId?.trim() || !sessionId.trim()) return;
  const redactedMessages = msgs.map(msg => ({
    ...msg,
    content: redactSecretReferences(msg.content),
  }));
  localStorage.setItem(_chatMessagesStorageKey(userId, sessionId), JSON.stringify(redactedMessages));
};

const loadChatSessions = (userId?: string): ChatSessionSummary[] => {
  if (!userId?.trim()) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(_storageKeyForUser(CHAT_SESSIONS_KEY, userId)) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (s): s is ChatSessionSummary => !!s && typeof s.id === 'string' && typeof s.title === 'string' && typeof s.updatedAt === 'string',
    );
  } catch {
    return [];
  }
};

const saveChatSessions = (userId: string | undefined, sessions: ChatSessionSummary[]) => {
  if (!userId?.trim()) return;
  localStorage.setItem(_storageKeyForUser(CHAT_SESSIONS_KEY, userId), JSON.stringify(sessions));
};

const loadActiveChatSessionId = (userId?: string): string => {
  if (!userId?.trim()) return '';
  try {
    const value = localStorage.getItem(_storageKeyForUser(CHAT_ACTIVE_SESSION_KEY, userId)) || '';
    return typeof value === 'string' ? value : '';
  } catch {
    return '';
  }
};

const saveActiveChatSessionId = (userId: string | undefined, sessionId: string) => {
  if (!userId?.trim()) return;
  localStorage.setItem(_storageKeyForUser(CHAT_ACTIVE_SESSION_KEY, userId), sessionId);
};

const loadArchivedChatSessionIds = (userId?: string): string[] => {
  if (!userId?.trim()) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(_storageKeyForUser(CHAT_ARCHIVED_SESSION_IDS_KEY, userId)) || '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const saveArchivedChatSessionIds = (userId: string | undefined, sessionIds: string[]) => {
  if (!userId?.trim()) return;
  localStorage.setItem(_storageKeyForUser(CHAT_ARCHIVED_SESSION_IDS_KEY, userId), JSON.stringify(sessionIds));
};

const loadLibraryFavorites = (userId?: string): string[] => {
  if (!userId?.trim()) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(_storageKeyForUser(LIBRARY_FAVORITES_KEY, userId)) || '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const saveLibraryFavorites = (userId: string | undefined, favoriteIds: string[]) => {
  if (!userId?.trim()) return;
  localStorage.setItem(_storageKeyForUser(LIBRARY_FAVORITES_KEY, userId), JSON.stringify(favoriteIds));
};

// ── Component ─────────────────────────────────────────────────────────────────

const AgentDashboard: React.FC<Props> = ({ onSelectTask, activeTaskId, onClearTask, user, onLogout, isAdmin = false, isLightMode = false }) => {
  const userId = user?.email || '';
  // Core state
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [archivedTasks, setArchivedTasks] = useState<AgentTask[]>([]);
  const [archivedKbs, setArchivedKbs] = useState<Array<{
    id: string;
    name: string;
    description?: string;
    status?: string;
    created_at?: string;
    updated_at?: string;
    document_count?: number;
    chunk_count?: number;
  }>>([]);
  const [archivedTasksLoading, setArchivedTasksLoading] = useState(false);
  const [archivedKbsLoading, setArchivedKbsLoading] = useState(false);
  const [goal, setGoal] = useState('');
  const [agentType, setAgentType] = useState('autonomous');
  const [taskPromptMode, setTaskPromptMode] = useState<TaskPromptMode>('standard');
  const [submitting, setSubmitting] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [view, setView] = useState<View>('home');
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourRequestId, setTourRequestId] = useState(0);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const [versionId, setVersionId] = useState(VERSIONS[0].id);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Projects
  const [projects, setProjects] = useState<Project[]>(() => loadProjects(userId));
  const projectsHydratedRef = useRef(false);
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [draggingChatSessionId, setDraggingChatSessionId] = useState<string | null>(null);
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectInstructions, setNewProjectInstructions] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [showAllProjects, setShowAllProjects] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AgentTask[]>([]);
  const [searchPage, setSearchPage] = useState(0);
  const [searchSortMode, setSearchSortMode] = useState<'recent' | 'oldest' | 'az' | 'za'>('recent');
  const [searchFilterStatus, setSearchFilterStatus] = useState<string>('all');

  // Library
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryFilterTool, setLibraryFilterTool] = useState<string>('all');
  const [libraryFilterTag, setLibraryFilterTag] = useState<string>('all');
  const [libraryDateFrom, setLibraryDateFrom] = useState('');
  const [libraryDateTo, setLibraryDateTo] = useState('');
  const [librarySortMode, setLibrarySortMode] = useState<'recent' | 'oldest' | 'az' | 'za'>('recent');
  const [libraryVisibleCount, setLibraryVisibleCount] = useState(LIBRARY_PAGE_SIZE);
  const [libraryItems, setLibraryItems] = useState<TaskLibraryItem[]>([]);
  const [libraryItemsLoading, setLibraryItemsLoading] = useState(false);
  const [libraryPreviewItem, setLibraryPreviewItem] = useState<TaskLibraryItem | null>(null);
  const [libraryCopiedTaskId, setLibraryCopiedTaskId] = useState<string | null>(null);
  const [libraryFavoriteIds, setLibraryFavoriteIds] = useState<Set<string>>(() => new Set(loadLibraryFavorites(userId)));
  const [libraryScope, setLibraryScope] = useState<'mine' | 'shared'>('mine');
  const [sharedLibraryTasks, setSharedLibraryTasks] = useState<AgentTask[]>([]);
  const [sharedLibraryLoading, setSharedLibraryLoading] = useState(false);
  const [libraryShareCounts, setLibraryShareCounts] = useState<Record<string, number>>({});
  const [libraryShareTask, setLibraryShareTask] = useState<TaskLibraryItem | null>(null);
  const [libraryShareRecipient, setLibraryShareRecipient] = useState('');
  const [librarySharePermission, setLibrarySharePermission] = useState<'view'>('view');
  const [libraryShares, setLibraryShares] = useState<TaskShare[]>([]);
  const [librarySharesLoading, setLibrarySharesLoading] = useState(false);
  const [libraryShareSaving, setLibraryShareSaving] = useState(false);
  const [libraryRevokingShareId, setLibraryRevokingShareId] = useState<string | null>(null);
  const [libraryAccessMessage, setLibraryAccessMessage] = useState('');
  const libraryDeepLinkHandledRef = useRef(false);

  // Knowledge Base selection for task creation
  const [availableKbs, setAvailableKbs] = useState<Array<{
    id: string;
    name: string;
    description?: string;
    project_id?: string;
    status?: string;
    document_count?: number;
    chunk_count?: number;
  }>>([]);
  const [selectedKbIds, setSelectedKbIds] = useState<string[]>([]);
  const [suggestedTaskStarters, setSuggestedTaskStarters] = useState<SuggestedTaskStarter[]>([]);
  const [loadingSuggestedTaskStarters, setLoadingSuggestedTaskStarters] = useState(false);
  const [showAttachKbModal, setShowAttachKbModal] = useState(false);
  const [attachKbId, setAttachKbId] = useState<string | undefined>(undefined);
  const [showProjectKbModal, setShowProjectKbModal] = useState(false);
  const [projectKbName, setProjectKbName] = useState('');
  const [projectKbDescription, setProjectKbDescription] = useState('');
  const [projectKbSubmitting, setProjectKbSubmitting] = useState(false);

  // File attachment
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  // Chat mode
  const [chatSessions, setChatSessions] = useState<ChatSessionSummary[]>([]);
  const [activeChatSessionId, setActiveChatSessionId] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [copiedChatMessageIndex, setCopiedChatMessageIndex] = useState<number | null>(null);
  const [chatTaskSubmitting, setChatTaskSubmitting] = useState(false);
  const [archivedChatSessionIds, setArchivedChatSessionIds] = useState<Set<string>>(() => new Set(loadArchivedChatSessionIds(userId)));
  const [chatKbIds, setChatKbIds] = useState<string[]>([]);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const chatSessionsHydratedRef = useRef(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [activeDictationTarget, setActiveDictationTarget] = useState<DictationTarget | null>(null);
  const [dictationStatus, setDictationStatus] = useState<DictationStatus>('idle');
  const [dictationTranscript, setDictationTranscript] = useState('');
  const [dictationError, setDictationError] = useState<string | null>(null);
  const dictationTranscriptRef = useRef('');
  const dictationCommitRef = useRef(true);
  const dictationErrorRef = useRef(false);
  const [secretNameSuggestions, setSecretNameSuggestions] = useState<SecretNameSuggestion[]>([]);
  const [secretMentionContext, setSecretMentionContext] = useState<SecretMentionContext | null>(null);
  const [secretMentionDropdownPosition, setSecretMentionDropdownPosition] = useState<SecretMentionDropdownPosition | null>(null);

  // Scheduled tasks
  const [scheduledTasks, setScheduledTasks] = useState<ScheduledTask[]>(loadScheduledTasks);
  const [agentSchedules, setAgentSchedules] = useState<api.AgentScheduleRecord[]>([]);
  const [agentSchedulesLoading, setAgentSchedulesLoading] = useState(false);
  const [runningAgentScheduleId, setRunningAgentScheduleId] = useState<string | null>(null);
  const [agentDetailTargetId, setAgentDetailTargetId] = useState<string>('');
  const scheduledTasksRef = useRef<ScheduledTask[]>(scheduledTasks);
  // Tracks "YYYY-MM-DDTHH:MM" for each recurring task id to prevent duplicate fires
  const recurringLastFiredRef = useRef<Map<string, string>>(new Map());
  // Tracks one-time task ids currently being processed to prevent duplicate execution
  const processingOnceRef = useRef<Set<string>>(new Set());
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleGoal, setScheduleGoal] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTimeVal, setScheduleTimeVal] = useState('');
  const [scheduleIsRecurring, setScheduleIsRecurring] = useState(false);
  const [scheduleRecurringDays, setScheduleRecurringDays] = useState<number[]>([]);
  const [scheduleRecurringTime, setScheduleRecurringTime] = useState('');

  // Competitor URL dialog
  const [competitorDialogVisible, setCompetitorDialogVisible] = useState(false);
  const [competitorUrls, setCompetitorUrls] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [archiveActionTaskId, setArchiveActionTaskId] = useState<string | null>(null);
  const [archiveActionKbId, setArchiveActionKbId] = useState<string | null>(null);
  const restoreKbNameRef = useRef('');

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    saveProjects(userId, projects);
    if (!userId || !projectsHydratedRef.current) return;

    const persistProjects = async () => {
      try {
        await api.addMemory(
          JSON.stringify(projects),
          {
            namespace: PROJECTS_MEMORY_NAMESPACE,
            metadata: { entry_type: 'projects_state', version: 1, project_count: projects.length },
            userId,
          },
        );

        const snapshotsRes = await api.listMemory({
          namespace: PROJECTS_MEMORY_NAMESPACE,
          user_id: userId,
          limit: MAX_PROJECT_SNAPSHOTS + 1,
        });
        const snapshots: MemoryEntrySummary[] = snapshotsRes.data || [];
        const stale = snapshots.slice(MAX_PROJECT_SNAPSHOTS);
        if (stale.length > 0) {
          await Promise.all(stale.map((entry) => api.deleteMemory(entry.id)));
        }
      } catch {
        // Best-effort persistence: localStorage already has the latest state.
      }
    };

    void persistProjects();
  }, [projects, userId]);

  useEffect(() => {
    let cancelled = false;

    const hydrateProjects = async () => {
      projectsHydratedRef.current = false;
      if (!userId) {
        setProjects([]);
        projectsHydratedRef.current = true;
        return;
      }

      const localProjects = loadProjects(userId);
      let hasRemoteSnapshot = false;
      if (!cancelled) setProjects(localProjects);

      try {
        const res = await api.listMemory({
          namespace: PROJECTS_MEMORY_NAMESPACE,
          user_id: userId,
          limit: 1,
        });
        const latest: MemoryEntrySummary | undefined = (res.data || [])[0];
        if (latest?.text) {
          hasRemoteSnapshot = true;
          try {
            const parsed = JSON.parse(latest.text);
            if (Array.isArray(parsed)) {
              if (!cancelled) setProjects(parsed as Project[]);
              saveProjects(userId, parsed as Project[]);
            }
          } catch (parseErr) {
            console.warn('[Praxys] Failed to parse project snapshot from memory service:', parseErr);
          }
        }
      } catch {
        // Fall back to localStorage.
      } finally {
        if (!hasRemoteSnapshot && localProjects.length > 0) {
          try {
            await api.addMemory(
              JSON.stringify(localProjects),
              {
                namespace: PROJECTS_MEMORY_NAMESPACE,
                metadata: { entry_type: 'projects_state', version: 1, project_count: localProjects.length },
                userId,
              },
            );
          } catch {
            // Best-effort sync of local legacy data to server storage.
          }
        }
        if (!cancelled) projectsHydratedRef.current = true;
      }
    };

    void hydrateProjects();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const createChatSessionSummary = useCallback((seedTitle = 'New chat'): ChatSessionSummary => {
    const trimmed = seedTitle.trim();
    return {
      id: `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: trimmed || 'New chat',
      updatedAt: new Date().toISOString(),
    };
  }, []);

  const upsertChatSession = useCallback((sessionId: string, titleSeed?: string) => {
    if (!sessionId) return;
    const now = new Date().toISOString();
    setChatSessions(prev => {
      const existing = prev.find(s => s.id === sessionId);
      const nextTitle = (titleSeed || '').trim() || existing?.title || 'New chat';
      if (existing) {
        return prev
          .map(s => (s.id === sessionId ? { ...s, title: nextTitle, updatedAt: now } : s))
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      }
      return [{ id: sessionId, title: nextTitle, updatedAt: now }, ...prev]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    });
  }, []);

  const addTaskToProject = useCallback((projectId: string, taskId: string) => {
    if (!projectId || !taskId) return;
    setProjects(prev => prev.map(project =>
      project.id === projectId
        ? { ...project, taskIds: project.taskIds.includes(taskId) ? project.taskIds : [...project.taskIds, taskId] }
        : project
    ));
  }, []);

  const startNewChatSession = useCallback(() => {
    const session = createChatSessionSummary();
    setChatSessions(prev => [session, ...prev].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
    setArchivedChatSessionIds(prev => {
      if (!prev.has(session.id)) return prev;
      const next = new Set(prev);
      next.delete(session.id);
      return next;
    });
    setActiveChatSessionId(session.id);
    setChatMessages([]);
    setChatInput('');
    setView('chat');
    if (isMobileViewport) setIsSidebarOpen(false);
  }, [createChatSessionSummary, isMobileViewport]);

  const getSessionMessages = useCallback((sessionId: string): ChatMessage[] => {
    if (!sessionId) return [];
    if (sessionId === activeChatSessionId) {
      return chatInput.trim()
        ? [...chatMessages, { role: 'user' as const, content: chatInput.trim() }]
        : chatMessages;
    }
    return loadChatMessages(userId, sessionId);
  }, [activeChatSessionId, chatInput, chatMessages, userId]);

  const getLatestChatPrompt = useCallback((sessionId: string): string => {
    const messages = getSessionMessages(sessionId);
    return [...messages].reverse().find(msg => msg.role === 'user')?.content.trim() || '';
  }, [getSessionMessages]);

  const moveAwayFromSession = useCallback((sessionId: string, nextSessions: ChatSessionSummary[]) => {
    const visibleNextSessions = nextSessions.filter(session => !archivedChatSessionIds.has(session.id) && session.id !== sessionId);
    if (visibleNextSessions.length > 0) {
      const nextSessionId = visibleNextSessions[0].id;
      setActiveChatSessionId(nextSessionId);
      setChatMessages(loadChatMessages(userId, nextSessionId));
    } else if (nextSessions.length > 0) {
      // Fall back to the first remaining session even if archived
      const nextSessionId = nextSessions[0].id;
      setActiveChatSessionId(nextSessionId);
      setChatMessages(loadChatMessages(userId, nextSessionId));
    } else {
      // No remaining sessions — clear active without auto-creating a new one
      setActiveChatSessionId('');
      setChatMessages([]);
    }
    setChatInput('');
    setView('chat');
  }, [archivedChatSessionIds, userId]);

  const handleDeleteChatSession = useCallback((sessionId: string) => {
    if (!sessionId) return;
    if (userId?.trim()) {
      localStorage.removeItem(_chatMessagesStorageKey(userId, sessionId));
    }
    const remainingSessions = chatSessions.filter(session => session.id !== sessionId);
    setChatSessions(remainingSessions);
    setArchivedChatSessionIds(prev => {
      if (!prev.has(sessionId)) return prev;
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
    if (activeChatSessionId === sessionId) {
      moveAwayFromSession(sessionId, remainingSessions);
    }
    if (activeChatSessionId === sessionId) setChatInput('');
    message.success('Chat deleted');
  }, [activeChatSessionId, chatSessions, moveAwayFromSession, userId]);

  const handleArchiveChatSession = useCallback((sessionId: string) => {
    if (!sessionId) return;
    const exists = chatSessions.some(session => session.id === sessionId);
    if (!exists) return;
    setArchivedChatSessionIds(prev => {
      if (prev.has(sessionId)) return prev;
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
    if (activeChatSessionId === sessionId) {
      moveAwayFromSession(sessionId, chatSessions);
    }
    message.success('Chat archived');
  }, [activeChatSessionId, chatSessions, moveAwayFromSession]);

  useEffect(() => {
    chatSessionsHydratedRef.current = false;
    if (!userId) {
      setChatSessions([]);
      setActiveChatSessionId('');
      setChatMessages([]);
      setArchivedChatSessionIds(new Set());
      return;
    }
    let sessions = loadChatSessions(userId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    const archivedIds = new Set(loadArchivedChatSessionIds(userId));
    if (sessions.length === 0) {
      sessions = [createChatSessionSummary()];
      saveChatSessions(userId, sessions);
    }
    setArchivedChatSessionIds(archivedIds);
    let nextActiveSessionId = loadActiveChatSessionId(userId);
    if (!nextActiveSessionId || !sessions.some(s => s.id === nextActiveSessionId) || archivedIds.has(nextActiveSessionId)) {
      nextActiveSessionId = sessions.find(s => !archivedIds.has(s.id))?.id || '';
    }
    if (!nextActiveSessionId) {
      const replacementSession = createChatSessionSummary();
      sessions = [replacementSession, ...sessions];
      nextActiveSessionId = replacementSession.id;
    }
    setChatSessions(sessions);
    setActiveChatSessionId(nextActiveSessionId);
    setChatMessages(loadChatMessages(userId, nextActiveSessionId));
    chatSessionsHydratedRef.current = true;
  }, [createChatSessionSummary, userId]);

  useEffect(() => {
    if (!userId || !chatSessionsHydratedRef.current) return;
    saveChatSessions(userId, chatSessions);
  }, [chatSessions, userId]);

  useEffect(() => {
    if (!userId || !chatSessionsHydratedRef.current) return;
    saveArchivedChatSessionIds(userId, Array.from(archivedChatSessionIds));
  }, [archivedChatSessionIds, userId]);

  useEffect(() => {
    if (!userId || !chatSessionsHydratedRef.current || !activeChatSessionId) return;
    saveActiveChatSessionId(userId, activeChatSessionId);
    setChatMessages(loadChatMessages(userId, activeChatSessionId));
  }, [activeChatSessionId, userId]);

  const fetchTasks = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.listTasks({ limit: 50, user_id: userId });
      setTasks(res.data.tasks || []);
    } catch {}
  }, [userId]);

  const fetchSharedLibraryTasks = useCallback(async () => {
    if (!userId) {
      setSharedLibraryTasks([]);
      return;
    }
    setSharedLibraryLoading(true);
    try {
      const res = await api.getSharedLibraryTasks();
      setSharedLibraryTasks(res.data.tasks || []);
    } catch {
      setSharedLibraryTasks([]);
    } finally {
      setSharedLibraryLoading(false);
    }
  }, [userId]);

  const fetchLibraryItems = useCallback(async () => {
    if (!userId) {
      setLibraryItems([]);
      return;
    }
    setLibraryItemsLoading(true);
    try {
      const res = await api.listLibraryItems(libraryScope, { limit: 500 });
      setLibraryItems(res.data.library_items || []);
    } catch {
      setLibraryItems([]);
    } finally {
      setLibraryItemsLoading(false);
    }
  }, [libraryScope, userId]);

  const fetchAgentSchedules = useCallback(async () => {
    if (!userId) {
      setAgentSchedules([]);
      return;
    }
    setAgentSchedulesLoading(true);
    try {
      const res = await api.listAllAgentSchedules();
      setAgentSchedules(res.data.schedules || []);
    } catch {
      setAgentSchedules([]);
    } finally {
      setAgentSchedulesLoading(false);
    }
  }, [userId]);

  const loadSharesForTask = useCallback(async (taskId: string) => {
    setLibrarySharesLoading(true);
    try {
      const res = await api.getLibraryItemShares(taskId);
      setLibraryShares(res.data.shares || []);
    } catch {
      setLibraryShares([]);
    } finally {
      setLibrarySharesLoading(false);
    }
  }, []);

  // Stable callback to avoid recreating activateSession in ChatWorkspace on every render.
  const handleChatSessionChange = useCallback((id: string) => {
    setActiveChatSessionId(id);
    saveActiveChatSessionId(userId, id);
  }, [userId]);

  const fetchArchivedTasks = useCallback(async () => {
    if (!userId) return;
    setArchivedTasksLoading(true);
    try {
      const res = await api.listTasks({ status: 'ARCHIVED', limit: 100, user_id: userId });
      setArchivedTasks(res.data.tasks || []);
    } catch {
      message.error('Failed to load archived tasks');
    } finally {
      setArchivedTasksLoading(false);
    }
  }, [userId]);

  const fetchArchivedKbs = useCallback(async () => {
    if (!userId) return;
    setArchivedKbsLoading(true);
    try {
      const res = await api.listKnowledgeBases({ status: 'ARCHIVED', limit: 100, user_id: userId });
      setArchivedKbs(res.data.knowledge_bases || []);
    } catch {
      message.error('Failed to load archived knowledge bases');
    } finally {
      setArchivedKbsLoading(false);
    }
  }, [userId]);

  // Load available KBs for task creation selector
  const fetchKbs = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.listKnowledgeBases({ status: 'ACTIVE', user_id: userId });
      const knowledgeBases = res.data.knowledge_bases || [];
      setAvailableKbs(knowledgeBases);
      if (view === 'project' && activeProjectId) {
        setSelectedKbIds(
          knowledgeBases
            .filter((kb: { project_id?: string }) => kb.project_id === activeProjectId)
            .map((kb: { id: string }) => kb.id),
        );
      }
    } catch {}
  }, [activeProjectId, view, userId]);

  const getProjectKnowledgeBases = useCallback(
    (projectId: string) => availableKbs.filter(kb => kb.project_id === projectId),
    [availableKbs],
  );

  const loadSuggestedTaskStarters = useCallback(async () => {
    if (selectedKbIds.length === 0) {
      setSuggestedTaskStarters([]);
      return;
    }
    setLoadingSuggestedTaskStarters(true);
    try {
      const responses = await Promise.all(
        selectedKbIds.map(kbId => api.listKnowledgeBaseSuggestedTasks(kbId, { limit: 30 })),
      );
      const merged = responses.flatMap(res => res.data.suggested_tasks || []);
      const deduped = Array.from(new Map(merged.map((row: SuggestedTaskStarter) => [row.id, row])).values());
      setSuggestedTaskStarters(deduped);
    } catch {
      setSuggestedTaskStarters([]);
    } finally {
      setLoadingSuggestedTaskStarters(false);
    }
  }, [selectedKbIds]);

  const updateSecretMentionContext = useCallback((target: MentionTarget, text: string, cursorPosition: number) => {
    const beforeCursor = text.slice(0, cursorPosition);
    const match = beforeCursor.match(/(?:^|\s)@([A-Za-z0-9_]*)$/);
    if (!match) {
      setSecretMentionContext(prev => (prev?.target === target ? null : prev));
      return;
    }
    const query = match[1] || '';
    const start = cursorPosition - query.length - 1;
    setSecretMentionContext({ target, start, end: cursorPosition, query });
  }, []);

  const filteredSecretSuggestions = useMemo(() => {
    if (!secretMentionContext) return [];
    const sourceText = secretMentionContext.target === 'task' ? goal : chatInput;
    if (secretMentionContext.end > sourceText.length) return [];
    const activeSlice = sourceText.slice(secretMentionContext.start, secretMentionContext.end);
    if (!activeSlice.startsWith('@')) return [];
    const normalizedQuery = activeSlice.slice(1).trim().toUpperCase();
    const startsWithMatches = secretNameSuggestions.filter(secret =>
      secret.secret_name.toUpperCase().startsWith(normalizedQuery),
    );
    const containsMatches = secretNameSuggestions.filter(
      secret =>
        !secret.secret_name.toUpperCase().startsWith(normalizedQuery)
        && secret.secret_name.toUpperCase().includes(normalizedQuery),
    );
    return [...startsWithMatches, ...containsMatches].slice(0, 8);
  }, [chatInput, goal, secretMentionContext, secretNameSuggestions]);

  useEffect(() => {
    if (!secretMentionContext || filteredSecretSuggestions.length === 0) {
      setSecretMentionDropdownPosition(null);
      return;
    }
    const textarea = secretMentionContext.target === 'task' ? textareaRef.current : chatTextareaRef.current;
    if (!textarea) {
      setSecretMentionDropdownPosition(null);
      return;
    }
    const mentionIndex = Math.max(0, Math.min(secretMentionContext.start, textarea.value.length - 1));
    if (textarea.value.charAt(mentionIndex) !== '@') {
      setSecretMentionDropdownPosition(null);
      return;
    }
    const caret = getTextareaCaretPosition(textarea, mentionIndex + 1);
    const width = Math.min(
      getMentionDropdownWidth(textarea, secretNameSuggestions.map(secret => secret.secret_name)),
      Math.max(180, textarea.clientWidth - 24),
    );
    const minLeft = 12;
    const maxLeft = Math.max(minLeft, textarea.clientWidth - width - 12);
    const unclampedLeft = caret.left - 10;
    const left = Math.min(Math.max(unclampedLeft, minLeft), maxLeft);
    const top = Math.max(8, caret.top + caret.lineHeight + 6);
    setSecretMentionDropdownPosition({ left, top, width });
  }, [chatInput, filteredSecretSuggestions.length, goal, secretMentionContext, secretNameSuggestions]);

  useEffect(() => {
    fetchTasks();
    const t = setInterval(fetchTasks, 8000);
    return () => clearInterval(t);
  }, [fetchTasks]);

  useEffect(() => {
    if (view === 'library') {
      void fetchLibraryItems();
    }
  }, [fetchLibraryItems, view]);

  useEffect(() => {
    void fetchAgentSchedules();
  }, [fetchAgentSchedules]);

  useEffect(() => {
    if (view !== 'library') return;
    const ownedItems = libraryItems.filter(item => (item.created_by || '').trim().toLowerCase() === (userId || '').trim().toLowerCase());
    if (ownedItems.length === 0) {
      setLibraryShareCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        ownedItems.map(async item => {
          try {
            const res = await api.getLibraryItemShares(item.id);
            return [item.id, (res.data.shares || []).length] as const;
          } catch {
            return [item.id, 0] as const;
          }
        }),
      );
      if (!cancelled) setLibraryShareCounts(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [libraryItems, userId, view]);

  useEffect(() => { fetchKbs(); }, [fetchKbs]);
  useEffect(() => { loadSuggestedTaskStarters(); }, [loadSuggestedTaskStarters]);
  useEffect(() => {
    let cancelled = false;
    if (!userId?.trim()) {
      setSecretNameSuggestions([]);
      setSecretMentionContext(null);
      return;
    }
    (async () => {
      try {
        const res = await api.listSecretNames(userId);
        if (cancelled) return;
        setSecretNameSuggestions(res.data.names || []);
      } catch {
        if (cancelled) return;
        setSecretNameSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (view === 'profile') {
      fetchArchivedTasks();
      fetchArchivedKbs();
    }
  }, [fetchArchivedKbs, fetchArchivedTasks, view]);

  // Close context menu on any click outside
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
    };
  }, [contextMenu]);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Persist scheduled tasks to localStorage
  useEffect(() => {
    scheduledTasksRef.current = scheduledTasks;
    saveScheduledTasks(scheduledTasks);
  }, [scheduledTasks]);

  // Persist chat messages to localStorage
  useEffect(() => {
    if (!userId || !chatSessionsHydratedRef.current || !activeChatSessionId) return;
    saveChatMessages(userId, activeChatSessionId, chatMessages);
  }, [activeChatSessionId, chatMessages, userId]);

  useEffect(() => {
    setLibraryFavoriteIds(new Set(loadLibraryFavorites(userId)));
  }, [userId]);

  useEffect(() => {
    if (libraryDeepLinkHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') !== 'library') return;
    libraryDeepLinkHandledRef.current = true;
    setView('library');
    const linkedTaskId = params.get('taskId') || '';
    if (!linkedTaskId) return;
    api.getTask(linkedTaskId)
      .then((res) => {
        const task = res.data as AgentTask;
        if (task?.status === 'COMPLETED' && task.final_answer) {
          setLibraryPreviewItem({
            id: `linked-${task.id}`,
            root_task_id: task.root_task_id || task.id,
            task_id: task.id,
            source_task_id: task.id,
            source_type: 'TASK',
            type: 'final_answer',
            title: task.goal,
            content_markdown: task.final_answer,
            content_text: task.final_answer,
            metadata_json: '{}',
            created_at: task.completed_at || task.updated_at || task.created_at,
          });
          setLibraryAccessMessage('');
        } else {
          setLibraryAccessMessage('This Library result is not available yet.');
        }
      })
      .catch(() => {
        setLibraryAccessMessage('You do not have access to this Library result. Ask the owner to share it with you.');
      });
  }, []);

  useEffect(() => {
    saveLibraryFavorites(userId, Array.from(libraryFavoriteIds));
  }, [libraryFavoriteIds, userId]);

  useEffect(() => {
    setLibraryVisibleCount(LIBRARY_PAGE_SIZE);
  }, [libraryDateFrom, libraryDateTo, libraryFilterTag, libraryFilterTool, libraryQuery, libraryScope, librarySortMode, libraryItems]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobileViewport(mobile);
      if (!mobile) {
        setIsSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check every 30 seconds whether a scheduled task is due
  useEffect(() => {
    const check = async () => {
      const now = new Date();
      const currentDay = now.getDay();
      const hh = now.getHours().toString().padStart(2, '0');
      const mm = now.getMinutes().toString().padStart(2, '0');
      const currentMinuteKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}T${hh}:${mm}`;
      const toRemove: string[] = [];
      for (const st of scheduledTasksRef.current) {
        try {
          if (st.isRecurring) {
            if (
              st.recurringDays.includes(currentDay) &&
              st.recurringTime === `${hh}:${mm}` &&
              recurringLastFiredRef.current.get(st.id) !== currentMinuteKey
            ) {
              recurringLastFiredRef.current.set(st.id, currentMinuteKey);
              await api.executeTask(st.goal, st.agentType, '', user?.email || '');
              await fetchTasks();
            }
          } else {
            const scheduledAt = new Date(st.scheduledAt);
            const isWithinWindow = scheduledAt <= now && scheduledAt > new Date(now.getTime() - 60000);
            if (isWithinWindow && !processingOnceRef.current.has(st.id)) {
              processingOnceRef.current.add(st.id);
              await api.executeTask(st.goal, st.agentType, '', user?.email || '');
              await fetchTasks();
              toRemove.push(st.id);
            }
          }
        } catch {
          message.error(`Scheduled task failed: "${st.goal.slice(0, 40)}…"`);
          // Remove the processing lock so it won't silently stay stuck
          processingOnceRef.current.delete(st.id);
        }
      }
      if (toRemove.length > 0) {
        setScheduledTasks(prev => prev.filter(t => !toRemove.includes(t.id)));
      }
    };
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSearch = useCallback((q: string) => {
    if (!q.trim()) {
      setSearchResults(tasks);
      return;
    }
    const lower = q.toLowerCase();
    setSearchResults(tasks.filter(t =>
      t.goal.toLowerCase().includes(lower) || t.status.toLowerCase().includes(lower)
    ));
  }, [tasks]);

  useEffect(() => {
    if (view === 'search') handleSearch(searchQuery);
  }, [view, tasks, searchQuery, handleSearch]);

  useEffect(() => {
    setSearchPage(0);
  }, [searchQuery, searchFilterStatus, searchSortMode]);

  const handleCreate = async (goalOverride?: string, projectId?: string) => {
    const text = (goalOverride ?? goal).trim();
    if (!text) { message.warning('Enter a goal first'); return; }
    setSubmitting(true);
    try {
      const fileForDrive = attachedFile;
      const currentVersion = VERSIONS.find(v => v.id === versionId) ?? VERSIONS[0];
      const inputContext: Record<string, any> = {
        version_id: currentVersion.id,
        version_label: currentVersion.label,
      };
      if (fileForDrive) {
        // Upload the file to artifact service first, then pass the reference
        try {
          const uploadRes = await api.uploadContextFile(fileForDrive);
          inputContext.attached_artifact_id = uploadRes.data.artifact_id;
          inputContext.attached_filename = uploadRes.data.filename;
          inputContext.attached_size = uploadRes.data.size_bytes;
        } catch {
          // Fallback: at minimum record the filename so the agent knows a file was attached
          inputContext.attached_filename = fileForDrive.name;
          inputContext.attached_size = fileForDrive.size;
        }
      }
      const res = await api.executeTask(
        text,
        agentType,
        '',
        user?.email || '',
        '',
        inputContext,
        selectedKbIds,
        projectId || '',
        taskPromptMode,
      );
      const task: AgentTask = res.data;
      if (fileForDrive && user?.email) {
        void api.getGDriveStatus(user.email)
          .then((status) => {
            const instance = status.data.instance;
            if (status.data.connected && instance?.id) {
              return api.uploadFileToConnector(instance.id, fileForDrive, 'task', task.id, text);
            }
            return undefined;
          })
          .catch(() => {
            // Non-blocking: task creation should not fail because Drive mirroring failed.
          });
      }
      setGoal('');
      setAttachedFile(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      if (projectId) {
        // Stay in project view and add task to the project
        addTaskToProject(projectId, task.id);
        await fetchTasks();
      } else {
        setView('home');
        await fetchTasks();
        onSelectTask(task.id);
      }
    } catch (e: any) {
      message.error('Failed to create task: ' + (e?.response?.data?.detail || e?.message || 'unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const latestChatTaskPrompt = useMemo(
    () => getLatestChatPrompt(activeChatSessionId),
    [activeChatSessionId, getLatestChatPrompt],
  );

  const handleRunTaskFromChat = async (options?: { sessionId?: string; projectId?: string }) => {
    const sessionId = options?.sessionId || activeChatSessionId;
    const taskGoal = getLatestChatPrompt(sessionId).trim();
    if (!taskGoal) {
      message.warning('Enter a chat message or use a chat with a previous prompt first');
      return;
    }
    setChatTaskSubmitting(true);
    try {
      const currentVersion = VERSIONS.find(v => v.id === versionId) ?? VERSIONS[0];
      const taskProjectId = options?.projectId || '';
      const chatHistory = getSessionMessages(sessionId);
      const inputContext: Record<string, any> = {
        version_id: currentVersion.id,
        version_label: currentVersion.label,
        source_view: 'chat',
        chat_session_id: sessionId,
        chat_history: chatHistory,
      };
      const res = await api.executeTask(
        taskGoal,
        agentType,
        '',
        user?.email || '',
        '',
        inputContext,
        chatKbIds,
        taskProjectId,
        'standard',
      );
      const task: AgentTask = res.data;
      if (taskProjectId) {
        addTaskToProject(taskProjectId, task.id);
        setActiveProjectId(taskProjectId);
        setView('project');
        message.success('Task launched and added to project');
      } else {
        setView('home');
        onSelectTask(task.id);
        message.success('Task launched from chat');
      }
      await fetchTasks();
    } catch (e: any) {
      message.error('Failed to launch task: ' + (e?.response?.data?.detail || e?.message || 'unknown'));
    } finally {
      setChatTaskSubmitting(false);
    }
  };

  const handleCompetitorSubmit = () => {
    const urls = competitorUrls.trim();
    const goalText = urls
      ? `Scrape and compare pricing from these competitor websites:\n${urls}`
      : 'Scrape and compare pricing from competitor websites';
    setCompetitorDialogVisible(false);
    setCompetitorUrls('');
    handleCreate(goalText);
  };

  const handleChatSubmit = async () => {
    const text = chatInput.trim();
    if (!text || !activeChatSessionId) return;
    const userMsg = { role: 'user' as const, content: text };
    upsertChatSession(activeChatSessionId, text.slice(0, 80));
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatSubmitting(true);
    try {
      // If KBs are selected, search them and prepend as context
      let contextPrefix = '';
      if (chatKbIds.length > 0) {
        try {
          contextPrefix = await api.buildSelectedKnowledgeBasePromptContext(
            chatKbIds,
            text,
            CHAT_KB_SEARCH_LIMIT,
          );
        } catch {
          // KB search failed — proceed without context
        }
      }

      // Build conversation history for the LLM (already in {role, content} format)
      const history: api.ChatMessage[] = [...chatMessages, userMsg];
      // Inject KB context into the last user message if available
      const historyWithContext: api.ChatMessage[] = contextPrefix
        ? [
            ...history.slice(0, -1),
            { role: 'user', content: contextPrefix + text },
          ]
        : history;

      const res = await api.chatWithLLM(historyWithContext, { sessionId: activeChatSessionId });
      const reply: string = res.data.reply || '';
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: reply },
      ]);
      upsertChatSession(activeChatSessionId, text.slice(0, 80));
    } catch (e: any) {
      const isNetworkErr = e?.message === 'Network Error' || e?.code === 'ERR_NETWORK';
      const detail = e?.response?.data?.detail;
      const errorText = isNetworkErr
        ? 'The AI service is currently unavailable. Please ensure the backend is running and try again.'
        : detail
        ? `Sorry, I couldn't respond right now: ${detail}`
        : `Sorry, I couldn't respond right now: ${e?.message || 'unknown error'}`;
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: errorText },
      ]);
      upsertChatSession(activeChatSessionId, text.slice(0, 80));
    } finally {
      setChatSubmitting(false);
    }
  };

  const handleCopyChatMessage = async (content: string, index: number) => {
    if (!content.trim()) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopiedChatMessageIndex(index);
      window.setTimeout(() => {
        setCopiedChatMessageIndex(prev => (prev === index ? null : prev));
      }, 2000);
    } catch {
      message.error('Failed to copy response');
    }
  };

  const handleTaskChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = e.target.value;
    setGoal(nextValue);
    updateSecretMentionContext('task', nextValue, e.target.selectionStart ?? nextValue.length);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  };

  const handleChatChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = e.target.value;
    setChatInput(nextValue);
    updateSecretMentionContext('chat', nextValue, e.target.selectionStart ?? nextValue.length);
  };

  const handleSecretSuggestionSelect = (target: MentionTarget, secretName: string) => {
    const mention = secretMentionContext;
    if (!mention || mention.target !== target) return;
    if (target === 'task') {
      setGoal(prev => `${prev.slice(0, mention.start)}@${secretName} ${prev.slice(mention.end)}`);
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        const nextCursor = mention.start + secretName.length + 2;
        textareaRef.current.focus();
        textareaRef.current.selectionStart = nextCursor;
        textareaRef.current.selectionEnd = nextCursor;
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
      });
    } else {
      setChatInput(prev => `${prev.slice(0, mention.start)}@${secretName} ${prev.slice(mention.end)}`);
      requestAnimationFrame(() => {
        if (!chatTextareaRef.current) return;
        const nextCursor = mention.start + secretName.length + 2;
        chatTextareaRef.current.focus();
        chatTextareaRef.current.selectionStart = nextCursor;
        chatTextareaRef.current.selectionEnd = nextCursor;
      });
    }
    setSecretMentionContext(null);
  };

  const appendDictationText = useCallback((target: DictationTarget, spokenText: string) => {
    if (!spokenText.trim()) return;
    if (target === 'task') {
      setSecretMentionContext(prev => (prev?.target === 'task' ? null : prev));
      setGoal(prev => (prev.trim() ? `${prev.trim()} ${spokenText}` : spokenText));
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
      });
      return;
    }
    setSecretMentionContext(prev => (prev?.target === 'chat' ? null : prev));
    setChatInput(prev => (prev.trim() ? `${prev.trim()} ${spokenText}` : spokenText));
  }, []);

  const finalizeDictation = useCallback((commitTranscript: boolean) => {
    dictationCommitRef.current = commitTranscript;
    if (recognitionRef.current) {
      setDictationStatus('stopped');
      recognitionRef.current.stop();
      return;
    }
    setActiveDictationTarget(null);
    setDictationStatus('idle');
    setDictationError(null);
    setDictationTranscript('');
  }, []);

  const handleDictationCancel = useCallback(() => {
    dictationErrorRef.current = false;
    finalizeDictation(false);
  }, [finalizeDictation]);

  const handleDictationToggle = useCallback((target: DictationTarget) => {
    const recognizerWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    const RecognitionCtor = recognizerWindow.SpeechRecognition || recognizerWindow.webkitSpeechRecognition;
    if (!RecognitionCtor) {
      message.warning('Dictation is not supported in this browser.');
      return;
    }
    if (activeDictationTarget === target) {
      finalizeDictation(true);
      return;
    }
    recognitionRef.current?.stop();
    try {
      const recognition = new RecognitionCtor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      dictationTranscriptRef.current = '';
      dictationErrorRef.current = false;
      dictationCommitRef.current = true;
      setDictationStatus('loading');
      setDictationError(null);
      setDictationTranscript('');
      setActiveDictationTarget(target);
      recognition.onstart = () => {
        setDictationStatus('recording');
      };
      recognition.onresult = (event: SpeechRecognitionEventLike) => {
        const transcriptParts: string[] = [];
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          if (!result?.[0]?.transcript) continue;
          const segment = result[0].transcript.trim();
          if (segment) transcriptParts.push(segment);
          if (result.isFinal && segment) {
            dictationTranscriptRef.current = `${dictationTranscriptRef.current} ${segment}`.trim();
          }
        }
        if (transcriptParts.length === 0) return;
        const preview = [dictationTranscriptRef.current, transcriptParts.join(' ')].filter(Boolean).join(' ').trim();
        if (preview) setDictationTranscript(preview);
      };
      recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
        dictationErrorRef.current = true;
        dictationCommitRef.current = false;
        const permissionDenied = event?.error === 'not-allowed' || event?.error === 'service-not-allowed';
        if (permissionDenied) {
          setDictationStatus('permission-denied');
          setDictationError('To use dictation, allow microphone access for Praxys.ai and retry.');
        } else {
          setDictationStatus('error');
          setDictationError(event?.message || 'We could not continue dictation. Please try again.');
        }
      };
      recognition.onend = () => {
        if (recognitionRef.current === recognition) recognitionRef.current = null;
        const finalTranscript = dictationTranscriptRef.current.trim();
        if (dictationCommitRef.current && !dictationErrorRef.current && finalTranscript) {
          appendDictationText(target, finalTranscript);
        }
        if (!dictationErrorRef.current) {
          setDictationStatus('idle');
          setDictationError(null);
          setDictationTranscript('');
          setActiveDictationTarget(current => (current === target ? null : current));
        }
        dictationErrorRef.current = false;
        dictationTranscriptRef.current = '';
      };
      recognitionRef.current = recognition;
      recognition.start();
    } catch (e: any) {
      const permissionDenied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
      setActiveDictationTarget(permissionDenied ? target : null);
      setDictationStatus(permissionDenied ? 'permission-denied' : 'error');
      setDictationError(
        permissionDenied
          ? 'To use dictation, allow microphone access for Praxys.ai and retry.'
          : 'Unable to start dictation.',
      );
      if (!permissionDenied) {
        message.error('Unable to start dictation.');
      }
    }
  }, [activeDictationTarget, appendDictationText, finalizeDictation]);

  const handleContextMenu = (e: React.MouseEvent, taskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, targetId: taskId, targetType: 'task' });
  };

  const handleTaskKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, taskId: string) => {
    if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setContextMenu({ x: rect.left, y: rect.bottom, targetId: taskId, targetType: 'task' });
    }
  };

  const handleChatContextMenu = (e: React.MouseEvent, sessionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, targetId: sessionId, targetType: 'chat' });
  };

  const handleDelete = async (taskId: string) => {
    setContextMenu(null);
    setArchiveActionTaskId(taskId);
    try {
      await api.deleteTask(taskId);
      message.success('Task deleted');
      if (activeTaskId === taskId) {
        onClearTask?.();
      }
      setProjects(prev => prev.map(p => ({ ...p, taskIds: p.taskIds.filter(id => id !== taskId) })));
      await fetchTasks();
      if (view === 'profile') {
        await fetchArchivedTasks();
      }
    } catch {
      message.error('Failed to delete task');
    } finally {
      setArchiveActionTaskId(current => (current === taskId ? null : current));
    }
  };

  const handleArchive = async (taskId: string) => {
    setContextMenu(null);
    try {
      await api.archiveTask(taskId);
      message.success('Task archived');
      if (activeTaskId === taskId) {
        onClearTask?.();
      }
      await fetchTasks();
    } catch {
      message.error('Failed to archive task');
    }
  };

  const handleRestoreArchivedTask = async (taskId: string) => {
    setArchiveActionTaskId(taskId);
    try {
      await api.restoreTask(taskId);
      message.success('Task restored');
      await Promise.all([fetchTasks(), fetchArchivedTasks()]);
    } catch {
      message.error('Failed to restore task');
    } finally {
      setArchiveActionTaskId(current => (current === taskId ? null : current));
    }
  };

  const promptRestoreKbWithNewName = (kb: { id: string; name: string }) => {
    restoreKbNameRef.current = kb.name;
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Knowledge base name already exists',
      content: (
        <div>
          <p style={{ color: '#e5e7eb', marginBottom: 8 }}>
            Enter a new name to restore this archived knowledge base.
          </p>
          <input
            type="text"
            defaultValue={kb.name}
            placeholder="New knowledge base name"
            onChange={(e) => { restoreKbNameRef.current = e.target.value; }}
            style={{
              width: '100%',
              background: '#111827',
              border: '1px solid #374151',
              color: '#e5e7eb',
              borderRadius: 8,
              padding: '8px 10px',
            }}
          />
        </div>
      ),
      okText: 'Restore',
      onOk: async () => {
        const trimmed = restoreKbNameRef.current.trim();
        if (!trimmed) {
          message.warning('A knowledge base name is required to restore');
          return Promise.reject();
        }
        try {
          await api.restoreKnowledgeBase(kb.id, { name: trimmed });
          message.success('Knowledge base restored');
          await Promise.all([fetchKbs(), fetchArchivedKbs()]);
        } catch {
          message.error('Failed to restore knowledge base');
          return Promise.reject();
        }
      },
    });
  };

  const handleRestoreArchivedKb = async (kb: { id: string; name: string }) => {
    setArchiveActionKbId(kb.id);
    try {
      await api.restoreKnowledgeBase(kb.id);
      message.success('Knowledge base restored');
      await Promise.all([fetchKbs(), fetchArchivedKbs()]);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        promptRestoreKbWithNewName(kb);
      } else {
        message.error('Failed to restore knowledge base');
      }
    } finally {
      setArchiveActionKbId(null);
    }
  };

  const handlePermanentlyDeleteArchivedKb = (kb: { id: string; name: string }) => {
    Modal.confirm({
      className: 'praxys-dark-confirm-modal',
      title: 'Delete knowledge base permanently?',
      content: `This will permanently delete "${kb.name}" and all associated documents and chunks.`,
      okText: 'Delete permanently',
      okType: 'danger',
      onOk: async () => {
        setArchiveActionKbId(kb.id);
        try {
          await api.deleteKnowledgeBasePermanently(kb.id);
          message.success('Knowledge base deleted permanently');
          await Promise.all([fetchKbs(), fetchArchivedKbs()]);
        } catch {
          message.error('Failed to permanently delete knowledge base');
          return Promise.reject();
        } finally {
          setArchiveActionKbId(null);
        }
      },
    });
  };

  const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleScheduleSubmit = () => {
    const text = scheduleGoal.trim();
    if (!text) { message.warning('Enter a goal first'); return; }
    if (!scheduleIsRecurring) {
      if (!scheduleDate || !scheduleTimeVal) { message.warning('Enter a date and time'); return; }
      const scheduledDate = new Date(`${scheduleDate}T${scheduleTimeVal}`);
      if (scheduledDate <= new Date()) {
        message.warning('Please select a future date and time'); return;
      }
      const newTask: ScheduledTask = {
        id: `sched-${Date.now()}`,
        goal: text,
        agentType,
        isRecurring: false,
        scheduledAt: scheduledDate.toISOString(),
        recurringDays: [],
        recurringTime: '',
      };
      setScheduledTasks(prev => [...prev, newTask]);
    } else {
      if (scheduleRecurringDays.length === 0) { message.warning('Select at least one day'); return; }
      if (!scheduleRecurringTime) { message.warning('Enter a time'); return; }
      const newTask: ScheduledTask = {
        id: `sched-${Date.now()}`,
        goal: text,
        agentType,
        isRecurring: true,
        scheduledAt: '',
        recurringDays: scheduleRecurringDays,
        recurringTime: scheduleRecurringTime,
      };
      setScheduledTasks(prev => [...prev, newTask]);
    }
    setShowScheduleModal(false);
    setScheduleGoal('');
    setScheduleDate('');
    setScheduleTimeVal('');
    setScheduleIsRecurring(false);
    setScheduleRecurringDays([]);
    setScheduleRecurringTime('');
    message.success('Task scheduled!');
  };

  const handleDeleteScheduledTask = (id: string) => {
    setScheduledTasks(prev => prev.filter(t => t.id !== id));
  };

  const parseAgentScheduleInputs = (schedule: api.AgentScheduleRecord): Record<string, any> => {
    try {
      const parsed = JSON.parse(schedule.default_inputs_json || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const runnable = { ...parsed };
      delete runnable.__schedule;
      return runnable;
    } catch {
      return {};
    }
  };

  const handleRunAgentSchedule = async (schedule: api.AgentScheduleRecord) => {
    if (runningAgentScheduleId) return;
    setRunningAgentScheduleId(schedule.id);
    try {
      const res = await api.executeAgent(schedule.agent_id, parseAgentScheduleInputs(schedule), 'SCHEDULE');
      message.success('Schedule started');
      await fetchAgentSchedules();
      if (res.data.taskId) {
        fetchTasks();
        setView('home');
        onSelectTask(res.data.taskId);
        closeSidebarOnMobile();
      }
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Unable to run schedule');
    } finally {
      setRunningAgentScheduleId(null);
    }
  };

  const handleToggleAgentSchedule = async (schedule: api.AgentScheduleRecord) => {
    try {
      await api.updateAgentSchedule(schedule.agent_id, schedule.id, { enabled: !schedule.enabled });
      await fetchAgentSchedules();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Unable to update schedule');
    }
  };

  const toggleRecurringDay = (day: number) => {
    setScheduleRecurringDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
      if (!goal.trim()) {
        const newGoal = isTaskImageAttachment(file)
          ? `Analyze the attached image ${file.name}`
          : `Read the attached ${file.name} and analyze it`;
        setGoal(newGoal);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
          }
        }, 10);
      }
    }
    e.target.value = '';
  };

  const resetInputBoxState = useCallback(() => {
    setGoal('');
    setSelectedKbIds([]);
    setTaskPromptMode('standard');
    setAttachedFile(null);
    setShowScheduleModal(false);
    setScheduleGoal('');
    setScheduleDate('');
    setScheduleTimeVal('');
    setScheduleIsRecurring(false);
    setScheduleRecurringDays([]);
    setScheduleRecurringTime('');
    setSecretMentionContext(null);
    setView(prev => (prev === 'chat' ? 'home' : prev));
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // ── Project handlers ──────────────────────────────────────────────────────────

  const handleCreateProject = () => {
    const name = newProjectName.trim();
    if (!name) return;
    const project: Project = {
      id: `proj-${Date.now()}`,
      name,
      instructions: newProjectInstructions.trim() || undefined,
      taskIds: [],
      collapsed: false,
    };
    setProjects(prev => [...prev, project]);
    setNewProjectName('');
    setNewProjectInstructions('');
    setShowProjectModal(false);
    setShowAllProjects(true);
    // Navigate directly to the new project view
    setActiveProjectId(project.id);
    onClearTask?.();
    setGoal('');
    setView('project');
  };

  const handleAttachKnowledgeBaseToProject = async (projectId: string) => {
    if (!attachKbId) {
      message.warning('Select a Knowledge Base to attach');
      return;
    }
    try {
      await api.updateKnowledgeBase(attachKbId, { project_id: projectId });
      message.success('Knowledge Base attached to project');
      setShowAttachKbModal(false);
      setAttachKbId(undefined);
      await fetchKbs();
    } catch {
      message.error('Failed to attach Knowledge Base');
    }
  };

  const handleDetachKnowledgeBaseFromProject = async (kbId: string) => {
    try {
      await api.updateKnowledgeBase(kbId, { project_id: '' });
      message.success('Knowledge Base removed from project');
      await fetchKbs();
    } catch {
      message.error('Failed to remove Knowledge Base from project');
    }
  };

  const handleCreateProjectKnowledgeBase = async (projectId: string) => {
    if (!projectKbName.trim()) {
      message.warning('Name is required');
      return;
    }
    setProjectKbSubmitting(true);
    try {
      await api.createKnowledgeBase({
        name: projectKbName.trim(),
        description: projectKbDescription.trim(),
        project_id: projectId,
        user_id: userId,
      });
      message.success('Knowledge Base created and attached');
      setShowProjectKbModal(false);
      setProjectKbName('');
      setProjectKbDescription('');
      await fetchKbs();
    } catch {
      message.error('Failed to create Knowledge Base');
    } finally {
      setProjectKbSubmitting(false);
    }
  };

  const handleDropOnProject = (projectId: string) => {
    if (draggingTaskId) {
      setProjects(prev => prev.map(p => {
        if (p.id !== projectId) return p;
        if (p.taskIds.includes(draggingTaskId)) return p;
        return { ...p, taskIds: [...p.taskIds, draggingTaskId] };
      }));
      setDraggingTaskId(null);
      setDragOverProjectId(null);
      return;
    }
    if (draggingChatSessionId) {
      const sessionId = draggingChatSessionId;
      setDraggingChatSessionId(null);
      setDragOverProjectId(null);
      void handleRunTaskFromChat({ sessionId, projectId });
    }
  };

  const handleRemoveFromProject = (projectId: string, taskId: string) => {
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, taskIds: p.taskIds.filter(id => id !== taskId) } : p
    ));
  };

  const handleToggleProject = (projectId: string) => {
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, collapsed: !p.collapsed } : p
    ));
  };

  const handleDeleteProject = async (projectId: string) => {
    const attachedKnowledgeBases = getProjectKnowledgeBases(projectId);
    if (attachedKnowledgeBases.length > 0) {
      try {
        await Promise.all(
          attachedKnowledgeBases.map(kb => api.updateKnowledgeBase(kb.id, { project_id: '' })),
        );
        await fetchKbs();
      } catch {
        message.error('Failed to detach project Knowledge Bases');
        return;
      }
    }
    setProjects(prev => prev.filter(p => p.id !== projectId));
    if (activeProjectId === projectId) {
      setActiveProjectId(null);
      setView('home');
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────────

  const projectTaskIdSet = useMemo(
    () => new Set(projects.flatMap(p => tasksInProjectFolder(tasks, projects, p.id).map(task => task.id))),
    [projects, tasks],
  );

  const closeSidebarOnMobile = useCallback(() => {
    if (isMobileViewport) setIsSidebarOpen(false);
  }, [isMobileViewport]);

  const allTasks = useMemo(
    () => tasks.filter(task => !projectTaskIdSet.has(task.id)),
    [tasks, projectTaskIdSet],
  );
  const sortedChatSessions = useMemo(
    () => [...chatSessions].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [chatSessions],
  );
  const visibleChatSessions = useMemo(
    () => sortedChatSessions.filter(session => !archivedChatSessionIds.has(session.id)),
    [archivedChatSessionIds, sortedChatSessions],
  );
  const activeProjectIndex = useMemo(
    () => projects.findIndex(project => project.id === activeProjectId),
    [activeProjectId, projects],
  );
  const showingAllProjects = showAllProjects || (activeProjectIndex >= PROJECTS_VISIBLE_LIMIT && activeProjectIndex !== -1);
  const visibleProjects = useMemo(
    () => showingAllProjects ? projects : projects.slice(0, PROJECTS_VISIBLE_LIMIT),
    [projects, showingAllProjects],
  );
  const hasMoreProjects = projects.length > PROJECTS_VISIBLE_LIMIT;

  const renderTaskItem = (task: AgentTask, projectId?: string) => (
    <button
      key={task.id}
      className={`praxys-task-item${draggingTaskId === task.id ? ' praxys-task-item--dragging' : ''}`}
      onClick={() => { setView('home'); onSelectTask(task.id); closeSidebarOnMobile(); }}
      onContextMenu={(e) => handleContextMenu(e, task.id)}
      onKeyDown={(e) => handleTaskKeyDown(e, task.id)}
      draggable
      onDragStart={() => setDraggingTaskId(task.id)}
      onDragEnd={() => { setDraggingTaskId(null); setDragOverProjectId(null); }}
    >
      <div className="praxys-task-meta">
        {task.status === 'RUNNING' ? (
          <LoadingOutlined className="praxys-task-spinner-icon" style={{ color: '#3b82f6', fontSize: 8 }} />
        ) : (
          <span className="praxys-task-dot" style={{ background: STATUS_DOT_COLOR[task.status] || '#6b7280' }} />
        )}
        <span className="praxys-task-date">{new Date(task.created_at).toLocaleDateString()}</span>
        {projectId && (
          <button
            className="praxys-task-remove-from-project"
            onClick={(e) => { e.stopPropagation(); handleRemoveFromProject(projectId, task.id); }}
            title="Remove from project"
            aria-label="Remove from project"
          >×</button>
        )}
      </div>
      <div className="praxys-task-goal" title={task.goal}>{summarizeTaskPrompt(task.goal, 100)}</div>
    </button>
  );

  const renderInputBox = (projectId?: string) => {
    const projectKnowledgeBaseCount = projectId ? getProjectKnowledgeBases(projectId).length : 0;

    return (
      <div className="praxys-input-wrap" data-help="task-composer">
        {attachedFile && (
          <div className="praxys-attached-file">
            <PaperClipOutlined style={{ marginRight: 6 }} />
            <span className="praxys-attached-file-name">{attachedFile.name}</span>
            {isTaskImageAttachment(attachedFile) && (
              <span className="praxys-attached-file-type">Image</span>
            )}
            <button
              className="praxys-attached-file-remove"
              onClick={() => setAttachedFile(null)}
              aria-label="Remove attachment"
            >×</button>
          </div>
        )}
          <div className={`praxys-input-box${activeDictationTarget === 'task' ? ' praxys-input-box--dictation' : ''}`}>
            <div className="praxys-task-starter-row">
              <button
                type="button"
                className="praxys-task-clear-btn"
                onClick={resetInputBoxState}
                title="Clear input"
                aria-label="Clear input"
              >
                <CloseCircleOutlined />
              </button>
              {selectedKbIds.length > 0 && (
                <div className="praxys-suggested-task-wrap">
                  <span className="praxys-suggested-task-label">Use Suggested Task</span>
                  <Select
                    size="small"
                    bordered={false}
                    className="praxys-task-starter-select"
                    placeholder="Use Suggested Task"
                    loading={loadingSuggestedTaskStarters}
                    value={undefined}
                    onSelect={(selectedId: string) => {
                      const selected = suggestedTaskStarters.find(s => s.id === selectedId);
                      if (!selected) return;
                      const selectedPrompt = (selected.suggested_prompt || selected.description || selected.name || '').trim();
                      setGoal(selectedPrompt);
                      if (selected.recommended_mode === 'deep_search') setTaskPromptMode('deep_search');
                      else setTaskPromptMode('standard');
                      if (selected.suggested_knowledge_base_ids && selected.suggested_knowledge_base_ids.length > 0) {
                        setSelectedKbIds(selected.suggested_knowledge_base_ids);
                      }
                    }}
                    options={suggestedTaskStarters.map(task => ({
                      value: task.id,
                      label: `${task.name}${task.recommended_mode === 'deep_search' ? ' · Deep Search' : ''}`,
                    }))}
                    notFoundContent="No suggested tasks yet"
                    style={{ minWidth: 170, maxWidth: 240 }}
                  />
                </div>
              )}
              <Select
                size="small"
                bordered={false}
                className="praxys-task-starter-select"
                placeholder="Use Recent Task"
                value={undefined}
                onSelect={(taskId: string) => {
                  const recent = allTasks.find(t => t.id === taskId);
                  if (recent) {
                    setGoal(recent.goal || '');
                    setTaskPromptMode('standard');
                  }
                }}
                options={allTasks.slice(0, MAX_RECENT_TASKS).map(task => ({
                  value: task.id,
                  label: task.goal.length > 80 ? `${task.goal.slice(0, 80)}…` : task.goal,
                }))}
                notFoundContent="No recent tasks"
                style={{ minWidth: 150, maxWidth: 230 }}
              />
            </div>
            {projectId && projectKnowledgeBaseCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ color: '#93c5fd', fontSize: 12 }}>
                {projectKnowledgeBaseCount} project Knowledge Base{projectKnowledgeBaseCount > 1 ? 's' : ''} pre-selected by default
              </span>
            </div>
          )}
          <div className="praxys-secret-mention-anchor">
            <textarea
              ref={textareaRef}
              className="praxys-textarea"
              value={goal}
              onChange={handleTaskChange}
              onClick={e => updateSecretMentionContext('task', e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
              onKeyUp={e => updateSecretMentionContext('task', e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
              onBlur={() => window.setTimeout(() => {
                setSecretMentionContext(prev => (prev?.target === 'task' ? null : prev));
              }, 100)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey && secretMentionContext?.target === 'task' && filteredSecretSuggestions.length > 0) {
                  e.preventDefault();
                  handleSecretSuggestionSelect('task', filteredSecretSuggestions[0].secret_name);
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleCreate(undefined, projectId);
                }
              }}
              placeholder={projectId ? 'Add a task to this project…' : 'Describe what you want the agent to do…'}
              rows={2}
            />
            {secretMentionContext?.target === 'task' && filteredSecretSuggestions.length > 0 && secretMentionDropdownPosition && (
              <div
                className="praxys-secret-mention-list"
                role="listbox"
                aria-label="Secret mentions"
                style={{
                  left: secretMentionDropdownPosition.left,
                  top: secretMentionDropdownPosition.top,
                  width: secretMentionDropdownPosition.width,
                }}
              >
                {filteredSecretSuggestions.map(secret => (
                  <button
                    key={secret.secret_name}
                    type="button"
                    className="praxys-secret-mention-option"
                    onMouseDown={e => {
                      e.preventDefault();
                      handleSecretSuggestionSelect('task', secret.secret_name);
                    }}
                  >
                    <span className="praxys-secret-mention-name">@{secret.secret_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {activeDictationTarget === 'task' && (
            <WaveIndicator active={dictationStatus === 'recording'} className="praxys-input-wave" />
          )}
          <div className="praxys-input-footer">
            <div className="praxys-input-footer-left">
              <Select
                value={agentType}
                onChange={setAgentType}
                size="small"
                bordered={false}
                options={[
                  { value: 'autonomous', label: 'Autonomous' },
                  { value: 'supervisor', label: 'Supervisor' },
                ]}
                className="praxys-agent-select"
              />
              {availableKbs.length > 0 && (
                <Select
                  mode="multiple"
                  size="small"
                  placeholder="+ Knowledge Bases"
                  value={selectedKbIds}
                  onChange={setSelectedKbIds}
                  bordered={false}
                  style={{ minWidth: 160, maxWidth: 300, fontSize: 12 }}
                  className="praxys-kb-select"
                  dropdownClassName="praxys-kb-select-dropdown"
                  options={availableKbs.map(kb => ({
                    value: kb.id,
                    label: (
                      <div className="praxys-kb-option">
                        <span className="praxys-kb-option-name" >{kb.name}</span>
                        <span className="praxys-kb-option-meta">
                          {kb.document_count || 0} docs · {kb.chunk_count || 0} chunks
                        </span>
                      </div>
                    ),
                    title: kb.description,
                  }))}
                  maxTagCount={2}
                />
              )}
              <button
                className="praxys-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Attach document or image"
                aria-label="Attach document or image"
              >
                <PaperClipOutlined style={{ fontSize: 14 }} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept={TASK_ATTACHMENT_ACCEPT}
                onChange={handleFileChange}
              />
            </div>
            <div className="praxys-input-footer-right">
              {taskPromptMode === 'deep_search' && (
                <span className="praxys-deep-research-pill">
                  <span>Deep Research</span>
                  <button
                    type="button"
                    className="praxys-deep-research-pill-remove"
                    onClick={() => setTaskPromptMode('standard')}
                    aria-label="Remove Deep Research mode"
                    title="Remove Deep Research mode"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                className={`praxys-dictation-btn${activeDictationTarget === 'task' ? ' active' : ''}`}
                onClick={() => handleDictationToggle('task')}
                title={activeDictationTarget === 'task' ? 'Stop dictation' : 'Dictate task'}
                aria-label={activeDictationTarget === 'task' ? 'Stop dictation' : 'Dictate task'}
              >
                <AudioOutlined style={{ fontSize: 14 }} />
              </button>
              <Dropdown
                overlay={
                  <Menu className="praxys-more-menu">
                    <Menu.Item
                      key="chat"
                      icon={<MessageOutlined />}
                      onClick={() => {
                        setView('chat');
                        setIsSidebarOpen(false);
                      }}
                    >
                      Chat mode
                    </Menu.Item>
                    <Menu.Item
                      key="deep-research"
                      icon={<SearchOutlined />}
                      onClick={() => setTaskPromptMode(prev => (prev === 'deep_search' ? 'standard' : 'deep_search'))}
                    >
                      Deep Research{taskPromptMode === 'deep_search' ? ' ✓' : ''}
                    </Menu.Item>
                    <Menu.Item
                      key="schedule"
                      icon={<CalendarOutlined />}
                      onClick={() => { setScheduleGoal(goal.trim()); setShowScheduleModal(true); }}
                    >
                      Schedule Task
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
                placement="topRight"
              >
                <button className="praxys-more-btn" aria-label="More options">
                  <MoreOutlined style={{ fontSize: 16 }} />
                </button>
              </Dropdown>
              <button
                className={`praxys-send-btn${goal.trim() ? ' active' : ''}`}
                onClick={() => handleCreate(undefined, projectId)}
                disabled={submitting || !goal.trim()}
                aria-label="Send"
              >
                {submitting ? <span className="praxys-spinner" /> : <SendOutlined style={{ fontSize: 14 }} />}
              </button>
            </div>
          </div>
        </div>
        <p className="praxys-hint">
          Press <kbd className="praxys-kbd">Enter</kbd> to submit,&nbsp;
          <kbd className="praxys-kbd">Shift+Enter</kbd> for a new line
        </p>
        {(taskPromptMode === 'deep_search' || availableKbs.length > 0) && (
          <p className="praxys-kb-helper">
            {taskPromptMode === 'deep_search'
              ? 'Deep Research runs as a normal task with research-oriented findings, sources, gaps, and recommended next steps.'
              : 'Selected Knowledge Bases are searched during planning and reasoning.'}
          </p>
        )}
      </div>
    );
  };

  const renderProjectView = () => {
    const project = projects.find(p => p.id === activeProjectId);
    if (!project) return null;
    const projectTasks = tasksInProjectFolder(tasks, projects, project.id);
    const projectKnowledgeBases = getProjectKnowledgeBases(project.id);
    const attachableKnowledgeBases = availableKbs.filter(kb => kb.project_id !== project.id);
    return (
      <div className="praxys-project-view">
        <div className="praxys-project-view-header">
          <FolderOutlined className="praxys-view-icon" style={{ color: '#f59e0b' }} />
          <div>
            <h2 className="praxys-view-title">{project.name}</h2>
            {project.instructions && (
              <p className="praxys-project-view-instructions">{project.instructions}</p>
            )}
          </div>
        </div>

        <div className="praxys-project-input-section">
          {renderInputBox(project.id)}
        </div>

        <div className="praxys-project-tasks-section">
          <div className="praxys-project-tasks-label" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span>
              <BookOutlined style={{ marginRight: 6 }} />
              Knowledge Bases ({projectKnowledgeBases.length})
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="praxys-more-btn"
                onClick={() => setShowAttachKbModal(true)}
                disabled={attachableKnowledgeBases.length === 0}
              >
                Add existing
              </button>
              <button
                type="button"
                className="praxys-more-btn"
                onClick={() => setShowProjectKbModal(true)}
              >
                New KB
              </button>
            </div>
          </div>
          {projectKnowledgeBases.length === 0 ? (
            <div className="praxys-view-empty">
              No Knowledge Bases attached yet — add an existing Knowledge Base or create one for this project.
            </div>
          ) : (
            <div className="praxys-project-task-list">
              {projectKnowledgeBases.map(kb => (
                <div key={kb.id} className="praxys-project-task-card">
                  <div className="praxys-task-card-status">
                    <DatabaseOutlined style={{ color: '#3b82f6', fontSize: 10 }} />
                    <span className="praxys-task-card-badge">KB</span>
                    <span className="praxys-task-date">
                      {kb.document_count || 0} docs · {kb.chunk_count || 0} chunks
                    </span>
                    <button
                      type="button"
                      className="praxys-task-remove-from-project"
                      onClick={() => handleDetachKnowledgeBaseFromProject(kb.id)}
                      aria-label="Remove knowledge base from project"
                      title="Remove from project"
                    >
                      ×
                    </button>
                  </div>
                  <div className="praxys-task-card-goal">{kb.name}</div>
                  {kb.description && (
                    <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>
                      {kb.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="praxys-project-tasks-section">
          <div className="praxys-project-tasks-label">
            <ProjectOutlined style={{ marginRight: 6 }} />
            Tasks ({projectTasks.length})
          </div>
          {projectTasks.length === 0 ? (
            <div className="praxys-view-empty">
              No tasks yet — add your first task above.
            </div>
          ) : (
            <div className="praxys-project-task-list">
              {projectTasks.map(task => (
                <div
                  key={task.id}
                  className="praxys-project-task-card"
                  onClick={() => { setView('home'); onSelectTask(task.id); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelectTask(task.id)}
                >
                  <div className="praxys-task-card-status">
                    {task.status === 'RUNNING' ? (
                      <LoadingOutlined style={{ color: '#3b82f6', fontSize: 10 }} />
                    ) : (
                      <span className="praxys-task-dot" style={{ background: STATUS_DOT_COLOR[task.status] || '#6b7280' }} />
                    )}
                    <span className="praxys-task-card-badge">{task.status}</span>
                    <span className="praxys-task-date">{new Date(task.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="praxys-task-card-goal" title={task.goal}>{summarizeTaskPrompt(task.goal, 100)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMainArea = () => {
    if (activeTaskId && view === 'home') {
      return (
        <AgentActivityConsole
          taskId={activeTaskId}
          onBack={onClearTask ?? noop}
          onOpenTask={(nextTaskId) => onSelectTask(nextTaskId)}
          user={user}
          onLogout={onLogout}
          availableKbs={availableKbs}
          recentTasks={allTasks.slice(0, MAX_RECENT_TASKS).map(t => ({ id: t.id, goal: t.goal }))}
          onScheduleTask={(g) => { setScheduleGoal(g); setShowScheduleModal(true); }}
        />
      );
    }

    if (view === 'project') {
      return renderProjectView();
    }

    if (view === 'profile') {
      return (
        <ProfileView
          user={user}
          archivedTasks={archivedTasks}
          archivedKnowledgeBases={archivedKbs}
          loading={archivedTasksLoading}
          kbLoading={archivedKbsLoading}
          busyTaskId={archiveActionTaskId}
          busyKbId={archiveActionKbId}
          onRestoreTask={handleRestoreArchivedTask}
          onDeleteTask={handleDelete}
          onRestoreKnowledgeBase={handleRestoreArchivedKb}
          onDeleteKnowledgeBase={handlePermanentlyDeleteArchivedKb}
        />
      );
    }

    if (view === 'help') {
      return <HelpView onAsk={() => setHelpOpen(true)} onStartTour={() => setTourRequestId(id => id + 1)} />;
    }

    if (view === 'admin') {
      return <AdminView user={user} />;
    }

    if (view === 'features') {
      return (
        <div style={{ height: '100%', background: '#2a2a2a', overflow: 'auto' }}>
          <FeatureRequestsHub user={user} isAdmin={isAdmin} />
        </div>
      );
    }

    if (view === 'search') {
      const allStatuses = Array.from(new Set(tasks.map(t => t.status))).sort((a, b) => a.localeCompare(b));
      const filteredSearchResults = searchResults
        .filter(task => searchFilterStatus === 'all' || task.status === searchFilterStatus)
        .sort((a, b) => {
          if (searchSortMode === 'az') return a.goal.localeCompare(b.goal);
          if (searchSortMode === 'za') return b.goal.localeCompare(a.goal);
          const left = new Date(a.created_at).getTime();
          const right = new Date(b.created_at).getTime();
          return searchSortMode === 'oldest' ? left - right : right - left;
        });
      const totalPages = Math.max(1, Math.ceil(filteredSearchResults.length / SEARCH_PAGE_SIZE));
      const safeSearchPage = Math.min(searchPage, totalPages - 1);
      const pageStart = safeSearchPage * SEARCH_PAGE_SIZE;
      const pagedResults = filteredSearchResults.slice(pageStart, pageStart + SEARCH_PAGE_SIZE);

      return (
        <div className="praxys-view-panel">
          <div className="praxys-view-header">
            <SearchOutlined className="praxys-view-icon" />
            <h2 className="praxys-view-title">Search Tasks</h2>
            <span className="praxys-view-subtitle">Search across all your tasks</span>
          </div>
          <div className="praxys-library-shell">
            <div className="praxys-library-search-row">
              <input
                className="praxys-search-input"
                placeholder="Search across all tasks…"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); handleSearch(e.target.value); }}
                autoFocus
              />
              <button className="praxys-search-btn" onClick={() => handleSearch(searchQuery)} aria-label="Search">
                <SearchOutlined />
              </button>
            </div>
            <div className="praxys-library-layout">
              <aside className="praxys-library-filters">
                <div className="praxys-library-filter-group">
                  <span className="praxys-library-filter-label">Status</span>
                  <Select
                    value={searchFilterStatus}
                    onChange={setSearchFilterStatus}
                    className="praxys-library-select"
                  >
                    <Select.Option value="all">All statuses</Select.Option>
                    {allStatuses.map(s => (
                      <Select.Option key={s} value={s}>{s}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="praxys-library-filter-group">
                  <span className="praxys-library-filter-label">Sort</span>
                  <Select
                    value={searchSortMode}
                    onChange={value => setSearchSortMode(value as 'recent' | 'oldest' | 'az' | 'za')}
                    className="praxys-library-select"
                  >
                    <Select.Option value="recent">Most recent</Select.Option>
                    <Select.Option value="oldest">Oldest first</Select.Option>
                    <Select.Option value="az">A–Z</Select.Option>
                    <Select.Option value="za">Z–A</Select.Option>
                  </Select>
                </div>
              </aside>
              <div className="praxys-library-content">
                {filteredSearchResults.length === 0 ? (
                  <div className="praxys-view-empty">No tasks found</div>
                ) : (
                  <>
                    <section className="praxys-library-section">
                      <h3 className="praxys-library-section-title">
                        {filteredSearchResults.length} result{filteredSearchResults.length !== 1 ? 's' : ''}
                      </h3>
                      <List
                        className="praxys-library-grid"
                        dataSource={pagedResults}
                        grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 4 }}
                        renderItem={task => (
                          <List.Item key={task.id}>
                            <div
                              className="praxys-library-card"
                              onClick={() => { setView('home'); onSelectTask(task.id); }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setView('home');
                                  onSelectTask(task.id);
                                }
                              }}
                            >
                              <div className="praxys-library-card-header">
                                <span
                                  className="praxys-task-dot"
                                  style={{ background: STATUS_DOT_COLOR[task.status] || '#6b7280', flexShrink: 0, marginTop: 4 }}
                                />
                                <div className="praxys-library-card-title-wrap">
                                  <div className="praxys-library-card-title">{task.goal}</div>
                                  <div className="praxys-library-card-meta">
                                    {new Date(task.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="praxys-library-card-tags">
                                <span className="praxys-library-tag">{task.status}</span>
                                {task.agent_type && (
                                  <span className="praxys-library-tag">{task.agent_type}</span>
                                )}
                                {task.task_type && (
                                  <span className="praxys-library-tag">{task.task_type}</span>
                                )}
                              </div>
                              <div className="praxys-library-card-actions">
                                <button
                                  className="praxys-library-action-btn"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setView('home');
                                    onSelectTask(task.id);
                                  }}
                                  title="Open task"
                                  aria-label="Open task"
                                >
                                  <CaretRightOutlined />
                                </button>
                              </div>
                            </div>
                          </List.Item>
                        )}
                      />
                    </section>
                    {totalPages > 1 && (
                      <div className="praxys-pagination">
                        <button
                          className="praxys-pagination-btn"
                          disabled={safeSearchPage === 0}
                          onClick={() => setSearchPage(p => Math.max(0, p - 1))}
                          aria-label="Previous page"
                        >
                          ‹
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => (
                          <button
                            key={i}
                            className={`praxys-pagination-btn${safeSearchPage === i ? ' active' : ''}`}
                            onClick={() => setSearchPage(i)}
                            aria-label={`Page ${i + 1}`}
                            aria-current={safeSearchPage === i ? 'page' : undefined}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <button
                          className="praxys-pagination-btn"
                          disabled={safeSearchPage === totalPages - 1}
                          onClick={() => setSearchPage(p => Math.min(totalPages - 1, p + 1))}
                          aria-label="Next page"
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (view === 'library') {
      const sourceLibraryTasks = libraryItems;
      const normalizeTag = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '-');
      const prettyTag = (value: string) => value.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const readTaskTags = (task: TaskLibraryItem): string[] => {
        if (Array.isArray(task.tags) && task.tags.length > 0) {
          return Array.from(new Set(task.tags.filter(Boolean).map(normalizeTag)));
        }
        try {
          const parsed = JSON.parse(task.tags_json || '[]');
          if (Array.isArray(parsed) && parsed.length > 0) {
            return Array.from(new Set(parsed.filter(Boolean).map(String).map(normalizeTag)));
          }
        } catch {
          // no-op
        }
        const tags = [
          task.source_type || task.type || '',
        ]
          .filter(Boolean)
          .map(normalizeTag);
        return Array.from(new Set(tags));
      };
      const toolOptions = Array.from(new Set(sourceLibraryTasks.map(task => task.source_type || task.type).filter(Boolean))).sort((a, b) => a.localeCompare(b));
      const tagOptions = Array.from(new Set(sourceLibraryTasks.flatMap(readTaskTags))).sort((a, b) => a.localeCompare(b));
      const query = libraryQuery.trim().toLowerCase();
      const fromMs = libraryDateFrom ? new Date(`${libraryDateFrom}T00:00:00`).getTime() : null;
      const toMs = libraryDateTo ? new Date(`${libraryDateTo}T23:59:59.999`).getTime() : null;
      const libraryTasks = sourceLibraryTasks
        .filter(task => {
          const completedMs = new Date(task.created_at).getTime();
          if (fromMs !== null && completedMs < fromMs) return false;
          if (toMs !== null && completedMs > toMs) return false;
          if (libraryFilterTool !== 'all' && (task.source_type || task.type) !== libraryFilterTool) return false;
          const tags = readTaskTags(task);
          if (libraryFilterTag !== 'all' && !tags.includes(libraryFilterTag)) return false;
          if (!query) return true;
          const haystack = `${task.title} ${task.summary || ''} ${task.content_markdown || ''} ${task.content_text || ''} ${task.source_type || ''} ${tags.join(' ')}`.toLowerCase();
          return haystack.includes(query);
        })
        .sort((a, b) => {
          if (librarySortMode === 'az') return a.title.localeCompare(b.title);
          if (librarySortMode === 'za') return b.title.localeCompare(a.title);
          const left = new Date(a.created_at).getTime();
          const right = new Date(b.created_at).getTime();
          return librarySortMode === 'oldest' ? left - right : right - left;
        });
      const starredTasks = libraryTasks.filter(task => libraryFavoriteIds.has(task.id));
      const regularTasks = libraryScope === 'mine' ? libraryTasks.filter(task => !libraryFavoriteIds.has(task.id)) : libraryTasks;
      const visibleRegularTasks = regularTasks.slice(0, libraryVisibleCount);
      const hasMoreRegularTasks = regularTasks.length > visibleRegularTasks.length;
      const toggleFavorite = (taskId: string) => {
        setLibraryFavoriteIds(prev => {
          const next = new Set(prev);
          if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
          return next;
        });
      };
      const openPreview = (task: TaskLibraryItem) => setLibraryPreviewItem(task);
      const buildShareUrl = (taskId: string) => {
        const shareUrl = new URL(window.location.href);
        shareUrl.searchParams.set('view', 'library');
        shareUrl.searchParams.set('taskId', taskId);
        return shareUrl.toString();
      };
      const openShareModal = (task: TaskLibraryItem) => {
        setLibraryShareTask(task);
        setLibraryShareRecipient('');
        setLibrarySharePermission('view');
        setLibraryCopiedTaskId(null);
        void loadSharesForTask(task.id);
      };
      const submitShare = async () => {
        if (!libraryShareTask) return;
        const recipient = libraryShareRecipient.trim().toLowerCase();
        if (!recipient) {
          message.warning('Enter an email or user ID');
          return;
        }
        setLibraryShareSaving(true);
        try {
          await api.shareLibraryItem(libraryShareTask.id, {
            shared_with_user_id: recipient,
            permission: librarySharePermission,
          });
          message.success('Library result shared');
          await loadSharesForTask(libraryShareTask.id);
          setLibraryShareCounts(prev => ({
            ...prev,
            [libraryShareTask.id]: Math.max(prev[libraryShareTask.id] || 0, 1),
          }));
          const shareUrl = buildShareUrl(libraryShareTask.id);
          await navigator.clipboard.writeText(shareUrl).catch(() => undefined);
          setLibraryCopiedTaskId(libraryShareTask.id);
          setLibraryShareRecipient('');
        } catch (error: any) {
          message.error(error?.response?.data?.detail || 'Unable to share this Library result');
        } finally {
          setLibraryShareSaving(false);
        }
      };
      const revokeShare = async (share: TaskShare) => {
        if (!libraryShareTask) return;
        setLibraryRevokingShareId(share.id);
        try {
          await api.deleteLibraryItemShare(libraryShareTask.id, share.id);
          message.success('Access revoked');
          await loadSharesForTask(libraryShareTask.id);
          setLibraryShareCounts(prev => ({
            ...prev,
            [libraryShareTask.id]: Math.max((prev[libraryShareTask.id] || 1) - 1, 0),
          }));
        } catch (error: any) {
          message.error(error?.response?.data?.detail || 'Unable to revoke access');
        } finally {
          setLibraryRevokingShareId(null);
        }
      };
      const deleteLibraryItem = async (item: TaskLibraryItem) => {
        try {
          await api.deleteLibraryItem(item.id);
          message.success('Library item deleted');
          await fetchLibraryItems();
        } catch (error: any) {
          message.error(error?.response?.data?.detail || 'Unable to delete Library item');
        }
      };
      const renderLibraryCard = (task: TaskLibraryItem, isOwned = true) => {
        const safeId = task.id.replace(/[^a-zA-Z0-9_-]/g, '_');
        const content = task.content_markdown || task.content_html || task.content_text || '';
        const handleDownloadMd = () => {
          const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${task.title.slice(0, MAX_DOWNLOAD_FILENAME_LENGTH).replace(/[^a-z0-9_\- ]/gi, '_')}.md`;
          a.click();
          URL.revokeObjectURL(url);
        };
        const handleDownloadDocx = () => {
          const a = document.createElement('a');
          a.href = task.source_task_id ? `${api.TASK_SERVICE_URL}/tasks/${task.source_task_id}/export/docx` : '';
          if (!a.href) return;
          a.download = `praxys-result-${safeId}.docx`;
          a.click();
        };
        const handleDownloadPdf = () => {
          const a = document.createElement('a');
          a.href = task.source_task_id ? `${api.TASK_SERVICE_URL}/tasks/${task.source_task_id}/export/pdf` : '';
          if (!a.href) return;
          a.download = `praxys-result-${safeId}.pdf`;
          a.click();
        };
        const libraryMenu = (
          <Menu
            theme="dark"
            className="praxys-more-menu"
            onClick={({ key, domEvent: event }) => {
              event.stopPropagation();
              if (key === 'md') handleDownloadMd();
              if (key === 'docx') handleDownloadDocx();
              if (key === 'pdf') handleDownloadPdf();
            }}
          >
            <Menu.Item key="md" icon={<FileTextOutlined />}>
              Markdown (.md)
            </Menu.Item>
            <Menu.Item key="docx" icon={<FileWordOutlined />}>
              Word (.docx)
            </Menu.Item>
            <Menu.Item key="pdf" icon={<FilePdfOutlined />}>
              PDF
            </Menu.Item>
          </Menu>
        );
        return (
          <div
            key={task.id}
            className="praxys-library-card"
            onClick={() => openPreview(task)}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPreview(task);
              }
            }}
          >
            <div className="praxys-library-card-header">
              <button
                className="praxys-library-favorite-btn"
                onClick={event => {
                  event.stopPropagation();
                  toggleFavorite(task.id);
                }}
                title={libraryFavoriteIds.has(task.id) ? 'Remove from favorites' : 'Add to favorites'}
                aria-label={libraryFavoriteIds.has(task.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                {libraryFavoriteIds.has(task.id) ? <StarFilled /> : <StarOutlined />}
              </button>
              <div className="praxys-library-card-title-wrap">
                                <div className="praxys-library-card-title">{task.title}</div>
                <div className="praxys-library-card-meta">
                  {new Date(task.created_at).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="praxys-library-card-tags">
              {!isOwned && <span className="praxys-library-tag">Shared With Me</span>}
              {isOwned && (libraryShareCounts[task.id] || 0) > 0 && <span className="praxys-library-tag">Shared</span>}
              <span className="praxys-library-tag">{task.source_status || 'Source available'}</span>
              {readTaskTags(task).slice(0, 4).map(tag => (
                <span key={`${task.id}-${tag}`} className="praxys-library-tag">{prettyTag(tag)}</span>
              ))}
            </div>
            <div className="praxys-library-card-actions">
              <button
                className="praxys-library-action-btn"
                onClick={event => {
                  event.stopPropagation();
                  openPreview(task);
                }}
                title="Preview"
                aria-label="Preview"
              >
                <EyeOutlined />
              </button>
              {isOwned && (
                <button
                  className="praxys-library-action-btn"
                  onClick={event => {
                    event.stopPropagation();
                    openShareModal(task);
                  }}
                  title="Share"
                  aria-label="Share"
                >
                  <ShareAltOutlined />
                </button>
              )}
              {isOwned && (
                <button
                  className="praxys-library-action-btn"
                  onClick={event => {
                    event.stopPropagation();
                    void deleteLibraryItem(task);
                  }}
                  title="Delete Library item"
                  aria-label="Delete Library item"
                >
                  <CloseOutlined />
                </button>
              )}
              <Dropdown overlay={libraryMenu} trigger={['click']} placement="bottomRight">
                <button
                  className="praxys-library-action-btn"
                  onClick={event => event.stopPropagation()}
                  title="Download"
                  aria-label="Download"
                >
                  <DownloadOutlined />
                </button>
              </Dropdown>
            </div>
          </div>
        );
      };

      return (
        <div className="praxys-view-panel">
          <div className="praxys-view-header">
            <DatabaseOutlined className="praxys-view-icon" />
            <h2 className="praxys-view-title">Library</h2>
            <span className="praxys-view-subtitle">Results from completed tasks</span>
          </div>
          {libraryAccessMessage && (
            <div className="praxys-view-empty" style={{ marginBottom: 12, color: '#fbbf24' }}>
              {libraryAccessMessage}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <Button
              className="praxys-agent-pill-btn--accent"
              onClick={() => setLibraryScope('mine')}
              style={{ opacity: libraryScope === 'mine' ? 1 : 0.62 }}
            >
              My Library
            </Button>
            <Button
              className="praxys-agent-pill-btn--accent"
              onClick={() => setLibraryScope('shared')}
              style={{ opacity: libraryScope === 'shared' ? 1 : 0.62 }}
            >
              Shared With Me
            </Button>
          </div>
          {sourceLibraryTasks.length === 0 ? (
            <div className="praxys-view-empty">
              {libraryScope === 'mine'
                ? 'No completed tasks yet. Finish a task to see its result here.'
                : libraryItemsLoading
                  ? 'Loading shared Library results...'
                  : 'No Library results have been shared with you yet.'}
            </div>
          ) : (
            <div className="praxys-library-shell">
              <div className="praxys-library-search-row">
                <input
                  className="praxys-search-input"
                  placeholder="Search by title or keyword…"
                  value={libraryQuery}
                  onChange={event => setLibraryQuery(event.target.value)}
                  aria-label="Search library"
                />
                <button className="praxys-search-btn" aria-label="Search Library">
                  <SearchOutlined />
                </button>
              </div>
              <div className="praxys-library-layout">
                <aside className="praxys-library-filters">
                  <div className="praxys-library-filter-group">
                    <span className="praxys-library-filter-label">Tool</span>
                    <Select
                      value={libraryFilterTool}
                      onChange={setLibraryFilterTool}
                      className="praxys-library-select"
                    >
                      <Select.Option value="all">All tools</Select.Option>
                      {toolOptions.map(tool => (
                        <Select.Option key={tool} value={tool}>{tool}</Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div className="praxys-library-filter-group">
                    <span className="praxys-library-filter-label">Tag</span>
                    <Select
                      value={libraryFilterTag}
                      onChange={setLibraryFilterTag}
                      className="praxys-library-select"
                    >
                      <Select.Option value="all">All tags</Select.Option>
                      {tagOptions.map(tag => (
                        <Select.Option key={tag} value={tag}>{prettyTag(tag)}</Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div className="praxys-library-filter-group">
                    <span className="praxys-library-filter-label">From</span>
                    <input
                      type="date"
                      className="praxys-library-date-input"
                      value={libraryDateFrom}
                      onChange={event => setLibraryDateFrom(event.target.value)}
                    />
                  </div>
                  <div className="praxys-library-filter-group">
                    <span className="praxys-library-filter-label">To</span>
                    <input
                      type="date"
                      className="praxys-library-date-input"
                      value={libraryDateTo}
                      onChange={event => setLibraryDateTo(event.target.value)}
                    />
                  </div>
                  <div className="praxys-library-filter-group">
                    <span className="praxys-library-filter-label">Sort</span>
                    <Select
                      value={librarySortMode}
                      onChange={value => setLibrarySortMode(value as 'recent' | 'oldest' | 'az' | 'za')}
                      className="praxys-library-select"
                    >
                      <Select.Option value="recent">Most recent</Select.Option>
                      <Select.Option value="oldest">Oldest first</Select.Option>
                      <Select.Option value="az">A–Z</Select.Option>
                      <Select.Option value="za">Z–A</Select.Option>
                    </Select>
                  </div>
                </aside>
                <div className="praxys-library-content">
                  {libraryTasks.length === 0 ? (
                    <div className="praxys-view-empty">No library results match your filters.</div>
                  ) : (
                    <>
                      {libraryScope === 'mine' && starredTasks.length > 0 && (
                        <section className="praxys-library-section">
                          <h3 className="praxys-library-section-title">Starred</h3>
                          <List
                            className="praxys-library-grid"
                            dataSource={starredTasks}
                            grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 4 }}
                            renderItem={task => (
                              <List.Item key={task.id}>
                                {renderLibraryCard(task, true)}
                              </List.Item>
                            )}
                          />
                        </section>
                      )}
                      <section className="praxys-library-section">
                        <h3 className="praxys-library-section-title">
                          {libraryScope === 'mine' ? 'All Results' : 'Shared With Me'}
                        </h3>
                        <List
                          className="praxys-library-grid"
                          dataSource={visibleRegularTasks}
                          grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 4 }}
                          renderItem={task => (
                            <List.Item key={task.id}>
                              {renderLibraryCard(task, libraryScope === 'mine')}
                            </List.Item>
                          )}
                        />
                        {hasMoreRegularTasks && (
                          <button
                            className="praxys-load-more-btn"
                            onClick={() => setLibraryVisibleCount(prev => prev + LIBRARY_PAGE_SIZE)}
                          >
                            Load more
                          </button>
                        )}
                      </section>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <Modal
            open={!!libraryPreviewItem}
            onCancel={() => setLibraryPreviewItem(null)}
            footer={null}
            width={860}
            bodyStyle={{ background: '#171717', maxHeight: '75vh', overflowY: 'auto' }}
            title={libraryPreviewItem ? `Preview - ${libraryPreviewItem.title}` : 'Preview'}
          >
            {libraryPreviewItem && (
              <div className="praxys-library-item-result">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{libraryPreviewItem.content_markdown || libraryPreviewItem.content_text || libraryPreviewItem.content_html || ''}</ReactMarkdown>
              </div>
            )}
          </Modal>
          <Modal
            open={!!libraryShareTask}
            onCancel={() => {
              setLibraryShareTask(null);
              setLibraryShares([]);
              setLibraryShareRecipient('');
              setLibraryCopiedTaskId(null);
            }}
            width={560}
            title="Share Library Result"
            bodyStyle={{ background: '#171717' }}
            footer={(
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  className="praxys-agent-pill-btn--accent"
                  onClick={() => {
                    setLibraryShareTask(null);
                    setLibraryShares([]);
                    setLibraryShareRecipient('');
                    setLibraryCopiedTaskId(null);
                  }}
                  style={{ opacity: 0.7 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="praxys-agent-pill-btn--accent"
                  onClick={() => void submitShare()}
                  disabled={libraryShareSaving}
                >
                  {libraryShareSaving ? 'Sharing...' : 'Share'}
                </button>
              </div>
            )}
          >
            {libraryShareTask && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Result</div>
                  <div style={{ color: '#f8fafc', fontWeight: 600 }}>{libraryShareTask.title}</div>
                </div>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 600 }}>Email / user ID</span>
                  <input
                    className="praxys-search-input"
                    value={libraryShareRecipient}
                    onChange={event => setLibraryShareRecipient(event.target.value)}
                    placeholder="recipient@example.com"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 600 }}>Permission</span>
                  <Select
                    value={librarySharePermission}
                    onChange={() => setLibrarySharePermission('view')}
                    className="praxys-library-select"
                  >
                    <Select.Option value="view">View only</Select.Option>
                  </Select>
                </label>
                {libraryCopiedTaskId === libraryShareTask.id && (
                  <div style={{ color: '#34d399', fontSize: 12 }}>
                    Library result shared. Link copied: {buildShareUrl(libraryShareTask.id)}
                  </div>
                )}
                <div style={{ borderTop: '1px solid #2f3541', paddingTop: 12 }}>
                  <div style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Manage Access</div>
                  {librarySharesLoading ? (
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>Loading shares...</div>
                  ) : libraryShares.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>Not shared with anyone yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {libraryShares.map(share => (
                        <div
                          key={share.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 10,
                            border: '1px solid #2f3541',
                            borderRadius: 10,
                            padding: '8px 10px',
                            background: '#111827',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: '#f8fafc', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {share.shared_with_user_id}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: 11 }}>View only</div>
                          </div>
                          <button
                            type="button"
                            className="praxys-library-action-btn"
                            disabled={libraryRevokingShareId === share.id}
                            onClick={() => void revokeShare(share)}
                            title="Revoke access"
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Modal>
        </div>
      );
    }

    if (view === 'knowledge') {
      return (
        <div style={{ height: '100%', background: '#2a2a2a', overflow: 'auto' }}>
          <KnowledgeBaseManager userId={userId} />
        </div>
      );
    }

    if (view === 'schedules') {
      const sortedSchedules = [...agentSchedules].sort((a, b) => {
        const left = new Date(a.next_run_at || a.created_at || 0).getTime();
        const right = new Date(b.next_run_at || b.created_at || 0).getTime();
        return left - right;
      });
      return (
        <div className="praxys-view-panel">
          <div className="praxys-view-header">
            <ClockCircleOutlined className="praxys-view-icon" />
            <h2 className="praxys-view-title">Agent Schedules</h2>
            <span className="praxys-view-subtitle">Scheduled agent runs across your visible agents</span>
          </div>
          {agentSchedulesLoading && sortedSchedules.length === 0 ? (
            <div className="praxys-view-empty">Loading schedules...</div>
          ) : sortedSchedules.length === 0 ? (
            <div className="praxys-view-empty">No agent schedules yet.</div>
          ) : (
            <List
              className="praxys-library-grid"
              dataSource={sortedSchedules}
              grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 4 }}
              renderItem={schedule => (
                <List.Item key={schedule.id}>
                  <div className="praxys-library-card praxys-agent-schedule-card">
                    <div className="praxys-library-card-header">
                      <ClockCircleOutlined className="praxys-agent-schedule-card-icon" />
                      <div className="praxys-library-card-title-wrap">
                        <div className="praxys-library-card-title">{schedule.agent_name || 'Agent'}</div>
                        <div className="praxys-library-card-meta">{schedule.cron_expression}</div>
                      </div>
                    </div>
                    <div className="praxys-library-card-tags">
                      <span className="praxys-library-tag">{schedule.enabled ? 'Enabled' : 'Disabled'}</span>
                      <span className="praxys-library-tag">{schedule.timezone || 'UTC'}</span>
                    </div>
                    <div className="praxys-agent-schedule-card-details">
                      <div>
                        <span>Last run</span>
                        <strong>{schedule.last_run_at ? new Date(schedule.last_run_at).toLocaleString() : 'Never'}</strong>
                      </div>
                      <div>
                        <span>Next run</span>
                        <strong>{schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString() : 'Not available'}</strong>
                      </div>
                    </div>
                    <div className="praxys-agent-schedule-actions">
                      <button
                        className="praxys-library-action-btn"
                        onClick={() => void handleRunAgentSchedule(schedule)}
                        disabled={runningAgentScheduleId === schedule.id || Boolean(runningAgentScheduleId)}
                        title="Run now"
                        aria-label="Run schedule now"
                      >
                        {runningAgentScheduleId === schedule.id ? <span className="praxys-spinner" /> : <PlayCircleOutlined />}
                      </button>
                      <button
                        className="praxys-library-action-btn"
                        onClick={() => void handleToggleAgentSchedule(schedule)}
                        title={schedule.enabled ? 'Disable schedule' : 'Enable schedule'}
                        aria-label={schedule.enabled ? 'Disable schedule' : 'Enable schedule'}
                      >
                        {schedule.enabled ? <CloseCircleOutlined /> : <CheckOutlined />}
                      </button>
                      <button
                        className="praxys-library-action-btn"
                        onClick={() => { setAgentDetailTargetId(schedule.agent_id); setView('agents'); }}
                        title="Open agent"
                        aria-label="Open agent"
                      >
                        <RocketOutlined />
                      </button>
                      <button
                        className="praxys-library-action-btn"
                        onClick={() => { setAgentDetailTargetId(schedule.agent_id); setView('agents'); }}
                        title="Edit schedule"
                        aria-label="Edit schedule"
                      >
                        <EditOutlined />
                      </button>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      );
    }

    if (view === 'agents') {
      return (
        <div style={{ height: '100%', background: '#171717', overflow: 'auto' }}>
          <AgentsView
            userId={userId}
            initialAgentId={agentDetailTargetId}
            projectFolders={projects}
            onPrepareProjectFolders={async () => {
              await api.addMemory(JSON.stringify(projects), {
                namespace: PROJECTS_MEMORY_NAMESPACE,
                metadata: { entry_type: 'projects_state', version: 1, project_count: projects.length },
                userId,
              });
            }}
            onOpenTask={(taskId) => {
              fetchTasks();
              setView('home');
              onSelectTask(taskId);
            }}
          />
        </div>
      );
    }

    if (view === 'chat') {
      return (
        <ChatWorkspace
          user={user}
          onTaskLaunched={(taskId) => {
            fetchTasks();
            onSelectTask(taskId);
          }}
          isLightMode={isLightMode}
          availableKbs={availableKbs}
          externalSessionId={activeChatSessionId}
          externalSessions={chatSessions}
          onSessionChange={handleChatSessionChange}
          onSessionsChange={setChatSessions}
        />
      );
    }

    if (view === 'terms') {
      return <TermsOfServiceView />;
    }

    if (view === 'privacy') {
      return <PrivacyPolicyView />;
    }

    // Default: home
    const currentVersion = VERSIONS.find(v => v.id === versionId) ?? VERSIONS[0];
    return (
      <div className="praxys-home-view">
        {/* Version picker — top right */}
        <div className="praxys-version-wrap">
          <Dropdown
            overlay={
              <Menu className="praxys-version-menu" selectedKeys={[versionId]}>
                {VERSIONS.map(v => (
                  <Menu.Item key={v.id} onClick={() => setVersionId(v.id)}>
                    <span className="praxys-version-menu-label">{v.label}</span>
                    <span className="praxys-version-menu-desc">{v.desc}</span>
                  </Menu.Item>
                ))}
              </Menu>
            }
            trigger={['click']}
            placement="bottomRight"
          >
            <button className="praxys-version-btn" aria-label="Select Praxys version">
              <span className="praxys-version-btn-label">{currentVersion.label}</span>
              <DownOutlined style={{ fontSize: 10 }} />
            </button>
          </Dropdown>
          <span className="praxys-version-tagline">{currentVersion.desc}</span>
        </div>

        <div className="praxys-center">
          <div className="praxys-hero">
            <div className="praxys-hero-icon">
              <img src={isLightMode ? logoHandBtnDark : logoHandBtn} alt="Praxys" className="praxys-hero-logo" />
            </div>
            <h1 className="praxys-hero-title">What should Praxys do?</h1>
            <p className="praxys-hero-sub">
              Autonomous AI agent — just describe your goal and Praxys will plan and execute it end-to-end.
            </p>
          </div>

          {renderInputBox()}

          <div className="praxys-suggestions">
            {SUGGESTIONS.map(s => (
              <button
                key={s.text}
                className="praxys-suggestion"
                onClick={() => s.hasDialog ? setCompetitorDialogVisible(true) : handleCreate(s.text)}
                disabled={submitting}
              >
                <span className="praxys-suggestion-icon">{s.icon}</span>
                <span className="praxys-suggestion-text">{s.text}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="praxys-home-footer">
          <button
            type="button"
            className="praxys-home-footer-link"
            onClick={() => {
              window.history.pushState({}, '', '/terms');
              setView('terms');
            }}
          >
            Terms of Service
          </button>
          <span className="praxys-home-footer-sep">·</span>
          <button
            type="button"
            className="praxys-home-footer-link"
            onClick={() => {
              window.history.pushState({}, '', '/privacy');
              setView('privacy');
            }}
          >
            Privacy Policy
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`praxys-shell${isSidebarOpen ? ' praxys-shell--sidebar-open' : ''}`}>
      {isMobileViewport && isSidebarOpen && (
        <button
          type="button"
          className="praxys-sidebar-backdrop"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Close navigation menu"
        />
      )}
      {/* ── Sidebar ───────────────────────────────────────────────────────────── */}
      <aside className={`praxys-sidebar${isSidebarOpen ? ' praxys-sidebar--open' : ''}`}>
        {/* Brand */}
        <button
          type="button"
          className="praxys-brand praxys-brand-link"
          onClick={() => { onClearTask?.(); setGoal(''); setView('home'); closeSidebarOnMobile(); }}
          aria-label="Go to home"
        >
          <img src={isLightMode ? logoHandBtnDark : logoHandBtn} alt="Praxys" className="praxys-brand-logo" />
          <span className="praxys-brand-name">Praxys</span>
          <span className="praxys-brand-dot">ai</span>
        </button>

        {/* New Task / Search / Library nav rows */}
        <div className="praxys-sidebar-top">
          <button
            className="praxys-new-btn"
            data-help="new-task"
            onClick={() => {
              onClearTask?.();
              setGoal('');
              setView('home');
              setTimeout(() => textareaRef.current?.focus(), 50);
              closeSidebarOnMobile();
            }}
          >
            <EditOutlined style={{ fontSize: 14 }} />
            New Task
          </button>
          <button
            className={`praxys-nav-row-btn${view === 'search' ? ' active' : ''}`}
            data-help="search"
            onClick={() => { setView('search'); setSearchResults(tasks); closeSidebarOnMobile(); }}
          >
            <SearchOutlined style={{ fontSize: 14 }} />
            Search
          </button>
          <button
            className={`praxys-nav-row-btn${view === 'library' ? ' active' : ''}`}
            data-help="library"
            onClick={() => { setView('library'); closeSidebarOnMobile(); }}
          >
            <DatabaseOutlined style={{ fontSize: 14 }} />
            Library
          </button>
          <button
            className={`praxys-nav-row-btn${view === 'schedules' ? ' active' : ''}`}
            data-help="schedules"
            onClick={() => { setView('schedules'); void fetchAgentSchedules(); closeSidebarOnMobile(); }}
          >
            <ClockCircleOutlined style={{ fontSize: 14 }} />
            Schedules
          </button>
          <button
            className={`praxys-nav-row-btn${view === 'knowledge' ? ' active' : ''}`}
            data-help="knowledge"
            onClick={() => { setView('knowledge'); fetchKbs(); closeSidebarOnMobile(); }}
          >
            <BookOutlined style={{ fontSize: 14 }} />
            Knowledge Bases
          </button>
          <button
            className={`praxys-nav-row-btn${view === 'agents' ? ' active' : ''}`}
            data-help="agents"
            onClick={() => { setAgentDetailTargetId(''); setView('agents'); closeSidebarOnMobile(); }}
          >
            <RocketOutlined style={{ fontSize: 14 }} />
            Agents
          </button>
          <button
            className={`praxys-nav-row-btn${view === 'features' ? ' active' : ''}`}
            data-help="features"
            onClick={() => { setView('features'); closeSidebarOnMobile(); }}
          >
            <StarOutlined style={{ fontSize: 14 }} />
            Feature Requests
          </button>
          <button
            className="praxys-nav-row-btn"
            data-help="help"
            onClick={() => { setHelpOpen(true); closeSidebarOnMobile(); }}
          >
            <QuestionCircleOutlined style={{ fontSize: 14 }} />
            Help
          </button>
        </div>

        <div className="praxys-sidebar-divider" />

        {/* Scrollable area: All Tasks + Projects */}
        <div className="praxys-sidebar-scroll">
          {/* All Tasks */}
          <div className="praxys-history-label">All Tasks</div>
          <div className="praxys-task-list">
            {allTasks.length === 0 ? (
              <div className="praxys-empty">No tasks yet</div>
            ) : (
              allTasks.map(task => renderTaskItem(task))
            )}
          </div>

          <div className="praxys-sidebar-divider" style={{ margin: '8px 12px' }} />

          {/* Chats */}
          <div className="praxys-projects-header">
            <div className="praxys-history-label" style={{ padding: '4px 16px' }}>
              <MessageOutlined style={{ marginRight: 6 }} />
              Chats
            </div>
            <button
              className="praxys-add-project-btn"
              onClick={startNewChatSession}
              title="New chat"
              aria-label="New chat"
            >
              <PlusOutlined style={{ fontSize: 13 }} />
            </button>
          </div>
          <div className="praxys-task-list">
            {visibleChatSessions.length === 0 ? (
              <div className="praxys-empty" style={{ fontSize: 11 }}>
                Click + to start a chat
              </div>
            ) : (
              visibleChatSessions.map(session => (
                <button
                  key={session.id}
                  className={`praxys-task-item${draggingChatSessionId === session.id ? ' praxys-task-item--dragging' : ''}`}
                  style={view === 'chat' && activeChatSessionId === session.id ? { background: '#2f2f2f' } : undefined}
                  onClick={() => {
                    setActiveChatSessionId(session.id);
                    setView('chat');
                    closeSidebarOnMobile();
                  }}
                  onContextMenu={(e) => handleChatContextMenu(e, session.id)}
                  draggable
                  onDragStart={() => setDraggingChatSessionId(session.id)}
                  onDragEnd={() => { setDraggingChatSessionId(null); setDragOverProjectId(null); }}
                >
                  <div className="praxys-task-meta">
                    <MessageOutlined style={{ color: '#8b949e', fontSize: 10 }} />
                    <span className="praxys-task-date">{new Date(session.updatedAt).toLocaleDateString()}</span>
                  </div>
                  <div className="praxys-task-goal">{session.title}</div>
                </button>
              ))
            )}
          </div>

          <div className="praxys-sidebar-divider" style={{ margin: '8px 12px' }} />

          {/* Projects */}
          <div className="praxys-projects-header">
            <div className="praxys-history-label" style={{ padding: '4px 16px' }}>Projects</div>
            <button
              className="praxys-add-project-btn"
              onClick={() => setShowProjectModal(true)}
              title="New project"
              aria-label="New project"
            >
              <FolderAddOutlined style={{ fontSize: 13 }} />
            </button>
          </div>

          <div className="praxys-projects-list">
            {projects.length === 0 && (
              <div className="praxys-empty" style={{ fontSize: 11 }}>
                Click <FolderAddOutlined /> to create a project
              </div>
            )}
            {visibleProjects.map(project => {
              const projectTasks = tasksInProjectFolder(tasks, projects, project.id);
              const isActive = activeProjectId === project.id && view === 'project';
              return (
                <div
                  key={project.id}
                  className={`praxys-project${dragOverProjectId === project.id ? ' praxys-project--drag-over' : ''}${isActive ? ' praxys-project--active' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOverProjectId(project.id); }}
                  onDragLeave={e => { if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget as Node)) setDragOverProjectId(null); }}
                  onDrop={() => handleDropOnProject(project.id)}
                >
                  <div className="praxys-project-header">
                    <button
                      className="praxys-project-select"
                      onClick={() => {
                        setActiveProjectId(project.id);
                        onClearTask?.();
                        setGoal('');
                        setView('project');
                        closeSidebarOnMobile();
                      }}
                    >
                      <span className="praxys-project-folder-icon">
                        {project.collapsed
                          ? <FolderOutlined style={{ fontSize: 16, color: '#fbbf24' }} />
                          : <FolderOpenOutlined style={{ fontSize: 16, color: '#fbbf24' }} />
                        }
                      </span>
                      <span className="praxys-project-name">{project.name}</span>
                      <span className="praxys-project-count">{new Set([...project.taskIds, ...projectTasks.map(task => task.id)]).size}</span>
                    </button>
                    <div className="praxys-project-actions">
                      <button
                        className="praxys-project-collapse"
                        onClick={e => { e.stopPropagation(); handleToggleProject(project.id); }}
                        title="Toggle tasks"
                        aria-label="Toggle tasks"
                      >
                        {project.collapsed
                          ? <CaretRightOutlined style={{ fontSize: 10 }} />
                          : <CaretDownOutlined style={{ fontSize: 10 }} />
                        }
                      </button>
                      <button
                        className="praxys-project-delete"
                        onClick={() => handleDeleteProject(project.id)}
                        title="Delete project"
                        aria-label="Delete project"
                      >×</button>
                    </div>
                  </div>
                  <div className={`praxys-project-body${project.collapsed ? ' is-collapsed' : ''}`}>
                    <div className="praxys-project-tasks">
                      {projectTasks.length === 0 ? (
                        <div className="praxys-project-empty">
                          {(draggingTaskId || draggingChatSessionId) ? '⬇ Drop here' : 'Drag tasks or chats here'}
                        </div>
                      ) : (
                        projectTasks.map(task => renderTaskItem(task, project.id))
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {hasMoreProjects && (
              <button
                className="praxys-projects-more-btn"
                onClick={() => setShowAllProjects(prev => !prev)}
                aria-label={showingAllProjects ? 'Show fewer projects' : 'Show more projects'}
              >
                {showingAllProjects ? 'Show less' : `More (${projects.length - PROJECTS_VISIBLE_LIMIT})`}
                <DownOutlined style={{ fontSize: 10, transform: showingAllProjects ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease' }} />
              </button>
            )}
          </div>

          {/* Scheduled Tasks */}
          {scheduledTasks.length > 0 && (
            <>
              <div className="praxys-sidebar-divider" style={{ margin: '8px 12px' }} />
              <div className="praxys-scheduled-header">
                <div className="praxys-history-label" style={{ padding: '4px 16px' }}>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />Scheduled
                </div>
              </div>
              <div className="praxys-scheduled-list">
                {scheduledTasks.map(st => (
                  <div key={st.id} className="praxys-scheduled-item">
                    <div className="praxys-scheduled-item-goal">{st.goal}</div>
                    <div className="praxys-scheduled-item-meta">
                      {st.isRecurring
                        ? `Every ${(st.recurringDays.length > 0 ? st.recurringDays.map(d => DAYS_OF_WEEK[d]) : ['day']).join(', ')} at ${st.recurringTime}`
                        : new Date(st.scheduledAt).toLocaleString()}
                    </div>
                    <button
                      className="praxys-scheduled-item-remove"
                      onClick={() => handleDeleteScheduledTask(st.id)}
                      title="Remove schedule"
                      aria-label="Remove schedule"
                    >×</button>
                  </div>
                ))}
              </div>
            </>
          )}

        </div>

        {/* User badge */}
        {user && (
          <>
            <div className="praxys-sidebar-divider praxys-sidebar-divider--bottom" />
            <Dropdown
              overlay={
                <Menu className="praxys-user-dropdown-menu">
                  <Menu.Item key="profile" icon={<UserOutlined />} onClick={() => setView('profile')}>
                    Profile
                  </Menu.Item>
                  <Menu.Divider />
                  {isAdmin && (
                    <>
                      <Menu.Item key="admin" icon={<SettingOutlined />} onClick={() => setView('admin')}>
                        Admin
                      </Menu.Item>
                      <Menu.Divider />
                    </>
                  )}
                  <Menu.Item key="help" icon={<QuestionCircleOutlined />} onClick={() => setHelpOpen(true)}>
                    Help
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="logout" icon={<LogoutOutlined />} danger onClick={onLogout}>
                    Log out
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement="topLeft"
              overlayClassName="praxys-user-dropdown"
            >
              <div className="praxys-user-badge" role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}>
                {user.picture ? (
                  <img src={user.picture} alt={`Profile picture of ${user.name}`} className="praxys-user-avatar" referrerPolicy="no-referrer" />
                ) : (
                  <div className="praxys-user-avatar praxys-user-avatar--initials">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="praxys-user-name">{user.name}</span>
              </div>
            </Dropdown>
          </>
        )}
      </aside>

      {/* ── Main area ─────────────────────────────────────────────────────────── */}
      <main className="praxys-main" data-help="main">
        {isMobileViewport && (
          <header className="praxys-mobile-header">
            <button
              type="button"
              className="praxys-mobile-menu-btn"
              onClick={() => setIsSidebarOpen(prev => !prev)}
              aria-label={isSidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
              title={isSidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {isSidebarOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
            <button
              type="button"
              className="praxys-mobile-home-btn"
              onClick={() => { onClearTask?.(); setView('home'); }}
              aria-label="Go to home"
            >
              <img src={isLightMode ? logoHandBtnDark : logoHandBtn} alt="" className="praxys-mobile-home-logo" />
              <span>Praxys.ai</span>
            </button>
            <button type="button" className="praxys-mobile-menu-btn" aria-label="Open Praxys Help"
              title="Help" onClick={() => setHelpOpen(true)}>
              <QuestionCircleOutlined />
            </button>
          </header>
        )}
        {renderMainArea()}
      </main>
      <HelpAssistant
        view={activeTaskId && view === 'home' ? 'task' : view}
        userId={userId}
        open={helpOpen}
        onClose={closeHelp}
        tourRequestId={tourRequestId}
        onOpenResources={() => setView('help')}
      />
      <DictationOverlay
        open={activeDictationTarget !== null}
        recording={dictationStatus === 'recording'}
        transcript={dictationTranscript}
        error={dictationStatus === 'error' || dictationStatus === 'permission-denied' ? dictationError : null}
        permissionDenied={dictationStatus === 'permission-denied'}
        onStop={() => finalizeDictation(true)}
        onCancel={handleDictationCancel}
      />

      {/* ── Right-click context menu ──────────────────────────────────────────── */}
      {contextMenu && (
        <div
          className="praxys-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.targetType === 'task' ? (
            <>
              <button className="praxys-context-menu-item praxys-context-menu-archive" onClick={() => handleArchive(contextMenu.targetId)}>
                Archive
              </button>
              <button className="praxys-context-menu-item praxys-context-menu-delete" onClick={() => handleDelete(contextMenu.targetId)}>
                Delete
              </button>
            </>
          ) : (
            <>
              <button className="praxys-context-menu-item praxys-context-menu-archive" onClick={() => { setContextMenu(null); handleArchiveChatSession(contextMenu.targetId); }}>
                Archive
              </button>
              <button className="praxys-context-menu-item praxys-context-menu-delete" onClick={() => { setContextMenu(null); handleDeleteChatSession(contextMenu.targetId); }}>
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Competitor URL dialog ─────────────────────────────────────────────── */}
      <Modal
        title="Competitor Website URLs"
        open={competitorDialogVisible}
        onOk={handleCompetitorSubmit}
        onCancel={() => { setCompetitorDialogVisible(false); setCompetitorUrls(''); }}
        okText="Start Task"
      >
        <p style={{ color: '#9ca3af', marginBottom: 12 }}>
          Enter the competitor website URLs to scrape and compare pricing (one per line):
        </p>
        <textarea
          className="praxys-textarea"
          style={{ background: '#2f2f2f', border: '1px solid #3f3f3f', borderRadius: 8, width: '100%', minHeight: 120, color: '#ececec', padding: '10px' }}
          value={competitorUrls}
          onChange={e => setCompetitorUrls(e.target.value)}
          placeholder={'https://competitor1.com\nhttps://competitor2.com\nhttps://competitor3.com'}
        />
      </Modal>

      {/* ── New Project modal ─────────────────────────────────────────────────── */}
      <Modal
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderAddOutlined style={{ color: '#f59e0b' }} />
            New Project
          </span>
        }
        open={showProjectModal}
        onOk={handleCreateProject}
        onCancel={() => { setShowProjectModal(false); setNewProjectName(''); setNewProjectInstructions(''); }}
        okText="Create Project"
        okButtonProps={{ disabled: !newProjectName.trim() }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
              Project name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              className="praxys-modal-input"
              placeholder="e.g. Market Research Q3"
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newProjectName.trim()) handleCreateProject(); }}
              autoFocus
            />
          </div>
          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
              Instructions <span style={{ color: '#6b7280', fontSize: 12 }}>(optional)</span>
            </label>
            <textarea
              className="praxys-modal-textarea"
              placeholder="Describe the project goal or any context for the tasks…"
              value={newProjectInstructions}
              onChange={e => setNewProjectInstructions(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Attach Knowledge Base"
        open={showAttachKbModal}
        onOk={() => activeProjectId && handleAttachKnowledgeBaseToProject(activeProjectId)}
        onCancel={() => { setShowAttachKbModal(false); setAttachKbId(undefined); }}
        okText="Attach"
        okButtonProps={{ disabled: !attachKbId }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <p style={{ color: '#9ca3af', margin: 0 }}>
            Attach an existing Knowledge Base to this project so it is available by default when creating tasks.
          </p>
          <Select
            value={attachKbId}
            onChange={setAttachKbId}
            placeholder="Select a Knowledge Base"
            options={availableKbs
              .filter(kb => kb.project_id !== activeProjectId)
              .map(kb => ({
                value: kb.id,
                label: `${kb.name} · ${kb.document_count || 0} docs · ${kb.chunk_count || 0} chunks`,
              }))}
          />
        </div>
      </Modal>

      <Modal
        title="Create Project Knowledge Base"
        open={showProjectKbModal}
        onOk={() => activeProjectId && handleCreateProjectKnowledgeBase(activeProjectId)}
        onCancel={() => {
          setShowProjectKbModal(false);
          setProjectKbName('');
          setProjectKbDescription('');
        }}
        okText="Create and Attach"
        confirmLoading={projectKbSubmitting}
        okButtonProps={{ disabled: !projectKbName.trim() }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
              Knowledge Base name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              className="praxys-modal-input"
              placeholder="e.g. Customer Interviews"
              value={projectKbName}
              onChange={e => setProjectKbName(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
              Description <span style={{ color: '#6b7280', fontSize: 12 }}>(optional)</span>
            </label>
            <textarea
              className="praxys-modal-textarea"
              placeholder="Describe what this project knowledge base should contain…"
              value={projectKbDescription}
              onChange={e => setProjectKbDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* ── Schedule Task modal ───────────────────────────────────────────────── */}
      <Modal
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined style={{ color: '#10a37f' }} />
            Schedule Task
          </span>
        }
        open={showScheduleModal}
        onOk={handleScheduleSubmit}
        onCancel={() => {
          setShowScheduleModal(false);
          setScheduleGoal('');
          setScheduleDate('');
          setScheduleTimeVal('');
          setScheduleIsRecurring(false);
          setScheduleRecurringDays([]);
          setScheduleRecurringTime('');
        }}
        okText="Schedule"
        okButtonProps={{ disabled: !scheduleGoal.trim() }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
          {/* Goal */}
          <div>
            <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>
              Task goal <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              className="praxys-modal-textarea"
              placeholder="Describe what you want the agent to do…"
              value={scheduleGoal}
              onChange={e => setScheduleGoal(e.target.value)}
              rows={3}
              autoFocus
            />
          </div>

          {/* Type toggle */}
          <div className="praxys-schedule-type-toggle">
            <button
              className={`praxys-schedule-type-btn${!scheduleIsRecurring ? ' active' : ''}`}
              onClick={() => setScheduleIsRecurring(false)}
              type="button"
            >
              <ClockCircleOutlined style={{ marginRight: 6 }} />One-time
            </button>
            <button
              className={`praxys-schedule-type-btn${scheduleIsRecurring ? ' active' : ''}`}
              onClick={() => setScheduleIsRecurring(true)}
              type="button"
            >
              <CalendarOutlined style={{ marginRight: 6 }} />Recurring
            </button>
          </div>

          {/* One-time: date + time */}
          {!scheduleIsRecurring && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>Date</label>
                <input
                  type="date"
                  className="praxys-modal-input praxys-schedule-date-input"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>Time</label>
                <input
                  type="time"
                  className="praxys-modal-input praxys-schedule-date-input"
                  value={scheduleTimeVal}
                  onChange={e => setScheduleTimeVal(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Recurring: days + time */}
          {scheduleIsRecurring && (
            <>
              <div>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 8 }}>Days</label>
                <div className="praxys-schedule-days">
                  {DAYS_OF_WEEK.map((day, i) => (
                    <button
                      key={day}
                      type="button"
                      className={`praxys-schedule-day-btn${scheduleRecurringDays.includes(i) ? ' active' : ''}`}
                      onClick={() => toggleRecurringDay(i)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#d1d5db', fontSize: 13, marginBottom: 6 }}>Time</label>
                <input
                  type="time"
                  className="praxys-modal-input praxys-schedule-date-input"
                  value={scheduleRecurringTime}
                  onChange={e => setScheduleRecurringTime(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AgentDashboard;

